/**
 * Omnissa UEM As-Built Documentation Tool
 *
 * Copyright (c) 2026 DeBruinOnline.net
 *
 * MIT License
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT.
 *
 * Developed by : DeBruinOnline.net
 * Built with   : Claude (Anthropic) — AI-assisted development
 * Version      : 1.0.0
 * UEM Version  : 2604
 */

import { useState, useCallback, useRef, useEffect, useMemo } from "react";

const C = {
  navy:"#0F1F3D", blue:"#1A56A0", sky:"#3B82C4", teal:"#00A8A8",
  mist:"#F4F7FB", white:"#FFFFFF", text:"#1A2340", muted:"#5A6A85",
  border:"#D1DAEA", success:"#16A34A", warn:"#D97706", error:"#DC2626",
  purple:"#7C3AED",
};
const NODE_COLORS={og:C.navy,profile:C.blue,app:C.teal,smartgroup:C.warn,policy:C.success,tag:C.purple,product:"#0891B2",user:"#BE185D"};
const NODE_ICONS ={og:"🏢",profile:"📋",app:"📦",smartgroup:"👥",policy:"🛡️",tag:"🏷️",product:"⚙️",user:"👤"};

const pIcon=p=>{const s=(typeof p==="string"?p:"").toLowerCase();if(s.includes("ios")||s.includes("apple"))return"🍎";if(s.includes("android"))return"🤖";if(s.includes("windows"))return"🪟";if(s.includes("macos")||s.includes("mac"))return"💻";return"📱";};
const str=v=>typeof v==="string"?v:typeof v==="number"?String(v):"";
const badge=(label,color=C.blue)=>(<span style={{background:color+"18",color,border:`1px solid ${color}40`,borderRadius:4,padding:"1px 7px",fontSize:11,fontWeight:600,whiteSpace:"nowrap"}}>{label}</span>);

function KV({data,mono=false}){
  if(!data||typeof data!=="object")return<span style={{fontFamily:"monospace",fontSize:12}}>{String(data)}</span>;
  const entries=Object.entries(data).filter(([,v])=>v!==null&&v!==undefined&&v!=="");
  if(!entries.length)return<span style={{color:C.muted,fontSize:12}}>—</span>;
  return(<table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}><tbody>
    {entries.map(([k,v])=>(<tr key={k} style={{borderBottom:`1px solid ${C.border}`}}>
      <td style={{padding:"4px 8px 4px 0",color:C.muted,fontWeight:600,width:"38%",verticalAlign:"top",whiteSpace:"nowrap"}}>{k}</td>
      <td style={{padding:"4px 0",color:C.text,fontFamily:mono?"monospace":"inherit",wordBreak:"break-all"}}>
        {typeof v==="object"?<pre style={{margin:0,fontSize:11,whiteSpace:"pre-wrap"}}>{JSON.stringify(v,null,2)}</pre>:String(v)}
      </td>
    </tr>))}
  </tbody></table>);
}

// Filter out internal/blob IDs and empty values for cleaner display
const INTERNAL_KEYS = /^(Id|id|UUID|uuid|Uuid|BlobId|blobId|.*BlobId|.*blob.*|.*Blob.*|.*CertId|.*certId|.*InternalId|.*internal_id|.*_id$|seq|Seq|.*Sequence|.*sequence)$/i;
function CleanKV({data, label="Inherited from parent", showAll=false}){
  if(!data||typeof data!=="object") return null;
  const entries = Object.entries(data).filter(([k,v])=>{
    if(v===null||v===undefined||v===""||v===0||v==="0") return false;
    if(!showAll && INTERNAL_KEYS.test(k)) return false;
    return true;
  });
  if(!entries.length) return <span style={{color:C.muted,fontSize:12,fontStyle:"italic"}}>No meaningful settings found</span>;
  return(
    <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
      <tbody>
        {entries.map(([k,v])=>(
          <tr key={k} style={{borderBottom:`1px solid ${C.border}`}}>
            <td style={{padding:"4px 8px 4px 0",color:C.muted,fontWeight:600,width:"38%",verticalAlign:"top",whiteSpace:"nowrap"}}>{k}</td>
            <td style={{padding:"4px 0",color:C.text,wordBreak:"break-all"}}>
              {typeof v==="object"
                ?<pre style={{margin:0,fontSize:11,whiteSpace:"pre-wrap",background:C.mist,padding:"4px 6px",borderRadius:3}}>{JSON.stringify(v,null,2)}</pre>
                :String(v)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Section({title,count,children,accent=C.teal,defaultOpen=true}){
  const[open,setOpen]=useState(defaultOpen);
  return(<div style={{marginBottom:16,border:`1px solid ${C.border}`,borderRadius:8,overflow:"hidden"}}>
    <button onClick={()=>setOpen(o=>!o)} style={{width:"100%",display:"flex",alignItems:"center",gap:10,padding:"11px 16px",background:C.mist,border:"none",cursor:"pointer",textAlign:"left",borderBottom:open?`1px solid ${C.border}`:"none"}}>
      <span style={{width:4,height:20,background:accent,borderRadius:2,flexShrink:0}}/>
      <span style={{fontWeight:700,fontSize:14,color:C.navy,flex:1}}>{title}</span>
      {count!==undefined&&badge(count,accent)}
      <span style={{color:C.muted,fontSize:12}}>{open?"▲":"▼"}</span>
    </button>
    {open&&<div style={{padding:16}}>{children}</div>}
  </div>);
}

const OG_LEVEL_COLORS=["#00A8A8","#1A56A0","#7C3AED","#D97706","#16A34A"];
const OG_LEVEL_ICONS=["🏢","🏬","📁","📂","📄"];
function OGTree({nodes,level=0}){
  const col=OG_LEVEL_COLORS[Math.min(level,OG_LEVEL_COLORS.length-1)];
  return(<div style={{marginLeft:level*22}}>
    {nodes.map(og=>(<div key={og.Id?.Value??og.Uuid} style={{marginBottom:3}}>
      <div style={{display:"flex",alignItems:"center",gap:8,padding:"5px 10px",
        background:level===0?C.navy+"0a":level===1?"#1A56A008":"transparent",
        borderLeft:`3px solid ${col}`,borderRadius:4}}>
        <span style={{fontSize:level===0?16:13,flexShrink:0}}>{OG_LEVEL_ICONS[Math.min(level,OG_LEVEL_ICONS.length-1)]}</span>
        <span style={{fontWeight:level<=1?700:500,color:C.navy,fontSize:level===0?14:13,flex:1}}>{og.Name}</span>
        <span style={{color:C.muted,fontSize:10}}>ID:{og.Id?.Value}</span>
        {og.GroupId&&og.GroupId!==''&&<span style={{background:C.sky+"18",color:C.sky,border:`1px solid ${C.sky}40`,borderRadius:3,padding:"0 5px",fontSize:10,fontWeight:600}}>{og.GroupId}</span>}
        {og.LocationGroupType&&<span style={{background:col+"18",color:col,border:`1px solid ${col}40`,borderRadius:3,padding:"0 5px",fontSize:10}}>{og.LocationGroupType}</span>}
        {og.children?.length>0&&<span style={{fontSize:10,color:C.muted,marginLeft:4}}>({og.children.length} child{og.children.length!==1?"ren":""})</span>}
      </div>
      {og.children?.length>0&&<OGTree nodes={og.children} level={level+1}/>}
    </div>))}
  </div>);
}

// ── Force graph ───────────────────────────────────────────────────────────────
// ── Force layout (DOM-only, stable positions) ────────────────────────────────
function useForceLayout(nodes, edges, width, height, gRef) {
  const posRef = useRef({});
  const rafRef = useRef(null);
  // Track which node-set this layout is for — only re-init when nodes actually change
  const nodeKeyRef = useRef("");

  useEffect(() => {
    if (!nodes.length) return;

    const newKey = nodes.map(n=>n.id).sort().join(",");

    // Only re-initialise positions when the set of nodes changes
    // This preserves user-dragged positions across React re-renders
    if (newKey !== nodeKeyRef.current) {
      nodeKeyRef.current = newKey;
      cancelAnimationFrame(rafRef.current);

      const typeOrder = ["og","smartgroup","profile","app","tag","product","user"];
      const byType = {};
      nodes.forEach(n => { (byType[n.type] || (byType[n.type] = [])).push(n); });
      const pos = {};

      // Keep existing positions for nodes that were already there
      nodes.forEach((n) => {
        if (posRef.current[n.id]) {
          pos[n.id] = { ...posRef.current[n.id] };
        } else {
          const ti = typeOrder.indexOf(n.type);
          const ring = byType[n.type] || [];
          const ringSize = ring.length;
          const i = ring.indexOf(n);
          // Place each type in its own sector of the canvas
          // OGs in center, others spread outward in dedicated sectors
          const sectorAngle = (2 * Math.PI * ti) / typeOrder.length;
          const withinRingAngle = (2 * Math.PI * i / Math.max(ringSize, 1));
          // Distance from center depends on type index - further out = different type
          const minR = ti === 0 ? 120 : 350 + ti * 150;
          const maxR = minR + Math.min(ringSize * 45, 350);
          const r = minR + (i / Math.max(ringSize-1, 1)) * (maxR - minR);
          // Spread within a sector
          const sectorSpread = Math.min((2 * Math.PI) / typeOrder.length * 0.85, Math.PI * 0.9);
          const angle = sectorAngle + (withinRingAngle * sectorSpread / (2*Math.PI)) - sectorSpread/2;
          const jitter = () => (Math.random() - 0.5) * 15;
          pos[n.id] = {
            x: width/2 + r * Math.cos(angle) + jitter(),
            y: height/2 + r * Math.sin(angle) + jitter(),
            vx: 0, vy: 0
          };
        }
      });
      posRef.current = pos;

      // Apply positions to DOM immediately
      const applyAll = () => {
        const g = gRef.current;
        if (!g) return;
        nodes.forEach(n => {
          const p = pos[n.id];
          if (!p) return;
          const el = g.querySelector(`[data-id="${n.id}"]`);
          if (el) el.setAttribute("transform", `translate(${p.x},${p.y})`);
        });
        edges.forEach((e,i) => {
          const s = pos[e.source], t = pos[e.target];
          if (!s||!t) return;
          const el = g.querySelector(`[data-edge="${i}"]`);
          if (!el) return;
          const line = el.querySelector("line");
          const txt  = el.querySelector("text");
          if (line){line.setAttribute("x1",s.x);line.setAttribute("y1",s.y);line.setAttribute("x2",t.x);line.setAttribute("y2",t.y);}
          if (txt){txt.setAttribute("x",(s.x+t.x)/2);txt.setAttribute("y",(s.y+t.y)/2-4);}
        });
      };

      // Wait for DOM to be ready before starting simulation
      const startSim = () => {
        applyAll();
        let tick = 0;
        const simulate = () => {
        const pos = posRef.current;
        const repel = 25000, k = 0.015, damp = 0.88;
        nodes.forEach(a => nodes.forEach(b => {
          if (a.id===b.id) return;
          const dx=pos[a.id].x-pos[b.id].x, dy=pos[a.id].y-pos[b.id].y;
          const d2=dx*dx+dy*dy+0.01, f=repel/d2;
          pos[a.id].vx+=dx*f; pos[a.id].vy+=dy*f;
        }));
        edges.forEach(e => {
          const s=pos[e.source], t=pos[e.target];
          if (!s||!t) return;
          const dx=t.x-s.x, dy=t.y-s.y, d=Math.sqrt(dx*dx+dy*dy)+0.01;
          const ideal=e.type==="parent"?120:180, f=(d-ideal)*k;
          s.vx+=dx/d*f; s.vy+=dy/d*f; t.vx-=dx/d*f; t.vy-=dy/d*f;
        });
        nodes.forEach(n => {
          const p=pos[n.id];
          p.vx+=(width/2-p.x)*0.0005; p.vy+=(height/2-p.y)*0.0005;
          p.vx*=damp; p.vy*=damp;
          p.x+=p.vx; p.y+=p.vy;
          p.x=Math.max(50,Math.min(width-50,p.x));
          p.y=Math.max(30,Math.min(height-30,p.y));
        });
        applyAll();
        tick++;
          if (tick < 800) rafRef.current = requestAnimationFrame(simulate);
        };
        rafRef.current = requestAnimationFrame(simulate);
      };
      // Small delay to ensure SVG elements are in DOM
      rafRef.current = requestAnimationFrame(() => requestAnimationFrame(startSim));
    } else {
      // Nodes unchanged — just re-apply current positions to DOM
      // (needed after React re-renders reset SVG element attributes)
      const pos = posRef.current;
      const g = gRef.current;
      if (g && pos) {
        nodes.forEach(n => {
          const p = pos[n.id];
          if (!p) return;
          const el = g.querySelector(`[data-id="${n.id}"]`);
          if (el) el.setAttribute("transform", `translate(${p.x},${p.y})`);
        });
        edges.forEach((e,i) => {
          const s=pos[e.source], t=pos[e.target];
          if (!s||!t) return;
          const el = g.querySelector(`[data-edge="${i}"]`);
          if (!el) return;
          const line=el.querySelector("line"), txt=el.querySelector("text");
          if (line){line.setAttribute("x1",s.x);line.setAttribute("y1",s.y);line.setAttribute("x2",t.x);line.setAttribute("y2",t.y);}
          if (txt){txt.setAttribute("x",(s.x+t.x)/2);txt.setAttribute("y",(s.y+t.y)/2-4);}
        });
      }
    }

    return () => cancelAnimationFrame(rafRef.current);
  });  // No dependency array — runs after every render, but only re-simulates when nodes change

  return posRef;
}


// Platform ID → naam mapping
const PLATFORM_MAP = {
  2:"Apple iOS", 4:"Android", 5:"WinMobile", 10:"Apple macOS",
  12:"Windows", 16:"ChromeOS", 21:"Android Work", 50:"Apple tvOS"
};
const PUSH_MODE = {0:"On Demand", 1:"Auto", 2:"Required"};
const APP_SOURCE = {0:"Internal", 1:"Public", 2:"Purchased"};

// ── Graph data builder ────────────────────────────────────────────────────────
function buildGraph(data, filter) {
  const nodes = [], edges = [];
  const add = (id, label, type, meta={}) => nodes.push({id, label, type, meta});

  (data.ogs||[]).forEach(og => {
    add(`og_${og.Id?.Value}`, og.Name, "og", {groupId:og.GroupId, type:og.LocationGroupType});
    const p = og.ParentLocationGroup?.Id?.Value;
    if (p) edges.push({source:`og_${p}`, target:`og_${og.Id?.Value}`, type:"parent", label:""});
  });
  (data.profiles||[]).forEach(p => {
    add(`prof_${p.Id?.Value}`, p.ProfileName??p.Name, "profile", {platform:str(p.Platform), status:str(p.Status)});
    const og = (data.ogs||[]).find(o => o.Id?.Value === parseInt(p.OrganizationGroupId) || o.Name === p.OrganizationGroupName);
    if (og) edges.push({source:`og_${og.Id?.Value}`, target:`prof_${p.Id?.Value}`, type:"has_profile", label:"profiel"});
  });
  (data.apps||[]).forEach((a, i) => {
    const id = `app_${a.Id?.Value??i}`;
    add(id, str(a.ApplicationName??a.AppName), "app", {platform:str(PLATFORM_MAP[a.Platform]??a.Platform), version:str(a.AppVersion), type:str(a.AppType??APP_SOURCE[a.ApplicationSource]??"Internal")});
    const og = (data.ogs||[]).find(o => o.Id?.Value === parseInt(a.LocationGroupId||a.OrganizationGroupId) || o.Name === a.OrganizationGroupName);
    if (og) edges.push({source:`og_${og.Id?.Value}`, target:id, type:"has_app", label:"app"});
    // App → Smart Group edges from SmartGroups array
    (a.SmartGroups||[]).forEach(sg => {
      const sgNodeId = `sg_${sg.Id}`;
      if (!nodes.find(n => n.id === sgNodeId)) {
        add(sgNodeId, sg.Name||`SG ${sg.Id}`, "smartgroup", {id:sg.Id, source:"app-assignment"});
      }
      if (!edges.find(e => e.source===sgNodeId && e.target===id)) {
        edges.push({source:sgNodeId, target:id, type:"sg_app", label:"app"});
      }
    });
  });
  (data.smartGroups||[]).forEach(sg => {
    const id = `sg_${sg.SmartGroupID}`;
    add(id, sg.Name, "smartgroup", {criteriaType:sg.CriteriaType, devices:sg.Devices??sg.DeviceCount, og:sg.ManagedByOrganizationGroupName??sg.OrganizationGroupName});
    const og = (data.ogs||[]).find(o =>
      o.Id?.Value === parseInt(sg.ManagedByOrganizationGroupId||sg.OrganizationGroupId) ||
      o.Name === (sg.ManagedByOrganizationGroupName||sg.OrganizationGroupName)
    );
    if (og) edges.push({source:`og_${og.Id?.Value}`, target:id, type:"has_sg", label:"smartgroup"});
    const det = data.sgDetails?.[sg.SmartGroupID];
    if (det?.Profiles) det.Profiles.forEach(pr => {
      const pn = (data.profiles||[]).find(p => p.Id?.Value === pr.Id || p.ProfileName === pr.Name);
      if (pn) edges.push({source:id, target:`prof_${pn.Id?.Value}`, type:"sg_profile", label:"→profiel"});
    });
    if (det?.Applications) det.Applications.forEach(ap => {
      const an = (data.apps||[]).find(a => a.Id?.Value === ap.Id || a.ApplicationName === ap.Name);
      if (an) edges.push({source:id, target:`app_${an.Id?.Value}`, type:"sg_app", label:"→app"});
    });
  });

  // Apps: link via AssignedSmartGroups from profile details (v2 API)
  // AND via OrganizationGroupId if present
  (data.apps||[]).forEach((a, i) => {
    const appNodeId = `app_${a.Id?.Value??i}`;
    // Link app → OG — try multiple fields the API might return
    if (!edges.find(e => e.target === appNodeId && e.type === "has_app")) {
      const og = (data.ogs||[]).find(o =>
        o.Id?.Value === parseInt(a.OrganizationGroupId) ||
        o.Id?.Value === parseInt(a.LocationGroupId) ||
        o.Id?.Value === parseInt(a.ManagedByOrganizationGroupId) ||
        o.Name === a.OrganizationGroupName ||
        o.GroupId === a.ManagedBy
      );
      // Fallback: link to root OG if no match found
      const target = og || (data.ogs||[])[0];
      if (target) edges.push({source:`og_${target.Id?.Value}`, target:appNodeId, type:"has_app", label:"app"});
    }
  });

  // Cross-link: use v2 profile detail AssignedSmartGroups to draw profile→SG edges
  // Structure: { SmartGroupId: 20805, Name: "SGRP_Android" }
  (data.profiles||[]).forEach(p => {
    const det = data.profileDetails?.[p.Id?.Value];
    const gen = det?.General;
    if (!gen?.AssignedSmartGroups?.length) return;
    const profNodeId = `prof_${p.Id?.Value}`;

    gen.AssignedSmartGroups.forEach(sgRef => {
      // sgRef is always an object: { SmartGroupId, Name }
      const sgId = sgRef.SmartGroupId ?? sgRef.Id ?? sgRef.id;
      if (!sgId) return;
      const sgNodeId = `sg_${sgId}`;
      const sgName = sgRef.Name ?? sgRef.name ?? `SG ${sgId}`;

      // Ensure SG node exists in graph
      if (!nodes.find(n => n.id === sgNodeId)) {
        add(sgNodeId, sgName, "smartgroup", {id:sgId, devices:"?", criteriaType:"assignment"});
      }

      // Ensure OG→SG edge exists
      const sgInList = (data.smartGroups||[]).find(s => s.SmartGroupID === sgId);
      if (sgInList) {
        const og = (data.ogs||[]).find(o =>
          o.Id?.Value === parseInt(sgInList.OrganizationGroupId) ||
          o.Name === sgInList.OrganizationGroupName
        );
        if (og && !edges.find(e => e.source===`og_${og.Id?.Value}` && e.target===sgNodeId)) {
          edges.push({source:`og_${og.Id?.Value}`, target:sgNodeId, type:"has_sg", label:"smartgroup"});
        }
      }

      // Draw profile→SG edge (direction: SG assigns profile)
      if (!edges.find(e => e.source===sgNodeId && e.target===profNodeId)) {
        edges.push({source:sgNodeId, target:profNodeId, type:"sg_profile", label:"→profiel"});
      }
    });

    // Also handle ExcludedSmartGroups — show as dashed red edge
    (gen.ExcludedSmartGroups||[]).forEach(sgRef => {
      const sgId = sgRef.SmartGroupId ?? sgRef.Id;
      if (!sgId) return;
      const sgNodeId = `sg_${sgId}`;
      const sgName = sgRef.Name ?? `SG ${sgId}`;
      if (!nodes.find(n => n.id === sgNodeId)) {
        add(sgNodeId, sgName, "smartgroup", {id:sgId, criteriaType:"excluded"});
      }
      if (!edges.find(e => e.source===sgNodeId && e.target===profNodeId && e.type==="sg_exclude")) {
        edges.push({source:sgNodeId, target:profNodeId, type:"sg_exclude", label:"excluded"});
      }
    });
  });
  (data.tags||[]).forEach(t => {
    const id = `tag_${t.TagId??t.Id?.Value}`;
    add(id, t.TagName, "tag", {type:t.TagType, og:t.LocationGroupName});
    const og = (data.ogs||[]).find(o => o.Id?.Value === parseInt(t.LocationGroupId));
    if (og) edges.push({source:`og_${og.Id?.Value}`, target:id, type:"has_tag", label:"tag"});
  });
  (data.products||[]).forEach((p, i) => {
    const id = `prod_${p.ID??i}`;
    add(id, str(p.Name), "product", {platform:str(p.Platform), status:str(p.Status)});
    const og = (data.ogs||[]).find(o => o.Id?.Value === parseInt(p.OrganizationGroupId) || o.Name === p.OrganizationGroupName);
    if (og) edges.push({source:`og_${og.Id?.Value}`, target:id, type:"has_product", label:"product"});
  });

  const active = new Set(filter.length ? filter : Object.keys(NODE_COLORS));
  const vis = new Set(nodes.filter(n => active.has(n.type)).map(n => n.id));
  return {
    nodes: nodes.filter(n => vis.has(n.id)),
    edges: edges.filter(e => vis.has(e.source) && vis.has(e.target))
  };
}

const edgeStyle = {
  parent:     {stroke:"#94A3B8", strokeWidth:1.5, dash:""},
  has_profile:{stroke:C.blue,    strokeWidth:1,   dash:"5,3"},
  has_app:    {stroke:C.teal,    strokeWidth:1,   dash:"5,3"},
  has_sg:     {stroke:C.warn,    strokeWidth:1,   dash:"5,3"},
  has_tag:    {stroke:C.purple,  strokeWidth:1,   dash:"3,3"},
  has_product:{stroke:"#0891B2", strokeWidth:1,   dash:"5,3"},
  sg_profile: {stroke:C.blue,    strokeWidth:1,   dash:"2,4"},
  sg_app:     {stroke:C.teal,    strokeWidth:1,   dash:"2,4"},
  sg_exclude: {stroke:C.error,   strokeWidth:1,   dash:"3,3"},
};
const nodeR = {og:26, profile:17, app:17, smartgroup:19, tag:14, product:16, user:14, policy:16};

// ── Relation Graph ────────────────────────────────────────────────────────────
function RelationGraph({data}) {
  const svgRef = useRef(null);
  const gRef = useRef(null);
  const gtRef = useRef(null); // transform group
  const panelRef = useRef(null);
  const [dims, setDims] = useState({w:900, h:700});
  const [filter, setFilter] = useState([]);
  const [selected, setSelected] = useState(null); // only state: which node is selected

  const pan = useRef({x:0, y:0});
  const zoom = useRef(1);
  const dragging = useRef(null);
  const panStart = useRef(null);

  useEffect(() => {
    const el = svgRef.current?.parentElement;
    if (!el) return;
    const obs = new ResizeObserver(() => setDims({w:el.offsetWidth, h:Math.max(700,el.offsetHeight)}));
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const {nodes, edges} = useMemo(() => buildGraph(data, filter), [data, filter]);
  const posRef = useForceLayout(nodes, edges, dims.w, dims.h, gRef);

  const types = Object.keys(NODE_COLORS);
  const typeLabels = useMemo(() => ({og:"OG's",profile:"Profiles",app:"Apps",smartgroup:"Smart Groups",tag:"Tags",product:"Products",user:"Users",policy:"Policies"}), []);

  // Apply highlight based on selected node — pure DOM, no re-render
  const applyHighlight = useCallback((selId) => {
    const g = gRef.current;
    if (!g) return;
    if (!selId) {
      g.querySelectorAll("[data-id]").forEach(el => { el.style.opacity = "1"; });
      g.querySelectorAll("[data-edge]").forEach(el => { el.style.opacity = "0.7"; });
      return;
    }
    const connected = new Set([selId]);
    edges.forEach(e => {
      if (e.source === selId) connected.add(e.target);
      if (e.target === selId) connected.add(e.source);
    });
    g.querySelectorAll("[data-id]").forEach(el => {
      el.style.opacity = connected.has(el.getAttribute("data-id")) ? "1" : "0.12";
    });
    g.querySelectorAll("[data-edge]").forEach(el => {
      const i = parseInt(el.getAttribute("data-edge"));
      const e = edges[i];
      el.style.opacity = (e && connected.has(e.source) && connected.has(e.target)) ? "0.85" : "0.05";
    });
  }, [edges]);

  // Render detail panel as HTML string — no React render
  const renderPanel = useCallback((selId) => {
    const panel = panelRef.current;
    if (!panel) return;
    if (!selId) {
      panel.innerHTML = `<div style="background:${C.mist};border:1px solid ${C.border};border-radius:8px;padding:20px;text-align:center;color:${C.muted};font-size:12px">
        <div style="font-size:28px;margin-bottom:8px">👆</div>
        Click a node to see relations and details
      </div>`;
      return;
    }
    const n = nodes.find(x => x.id === selId);
    if (!n) return;
    const col = NODE_COLORS[n.type];
    const connected = edges.filter(e => e.source === selId || e.target === selId);
    const metaHtml = n.meta ? Object.entries(n.meta).filter(([,v]) => v != null && v !== "")
      .map(([k,v]) => `<tr><td style="padding:3px 8px 3px 0;color:${C.muted};font-weight:600;font-size:11px;white-space:nowrap">${k}</td><td style="padding:3px 0;font-size:11px;word-break:break-all">${String(v)}</td></tr>`).join("") : "";
    const relHtml = connected.slice(0,15).map(e => {
      const other = nodes.find(x => x.id === (e.source===selId ? e.target : e.source));
      if (!other) return "";
      const ocol = NODE_COLORS[other.type];
      return `<div style="display:flex;align-items:center;gap:6px;padding:4px 0;border-bottom:1px solid ${C.border}">
        <div style="width:16px;height:16px;border-radius:50%;background:${ocol};display:flex;align-items:center;justify-content:center;font-size:9px;flex-shrink:0">${NODE_ICONS[other.type]}</div>
        <span style="flex:1;font-size:11px;word-break:break-word">${other.label}</span>
        <span style="font-size:9px;color:${C.muted};white-space:nowrap">${e.label||e.type}</span>
      </div>`;
    }).join("");
    panel.innerHTML = `
      <div style="background:${C.white};border:2px solid ${col};border-radius:8px;padding:12px;overflow:auto;max-height:500px">
        <div style="display:flex;align-items:center;gap:8px;margin-bottom:8px">
          <div style="width:24px;height:24px;border-radius:50%;background:${col};display:flex;align-items:center;justify-content:center;font-size:13px;flex-shrink:0">${NODE_ICONS[n.type]}</div>
          <div style="font-weight:700;font-size:13px;color:${C.navy};word-break:break-word">${n.label}</div>
        </div>
        <span style="background:${col}22;color:${col};border:1px solid ${col}44;border-radius:4px;padding:2px 8px;font-size:11px;font-weight:600">${typeLabels[n.type]}</span>
        ${metaHtml ? `<table style="width:100%;border-collapse:collapse;margin-top:8px">${metaHtml}</table>` : ""}
        ${connected.length ? `
          <div style="margin-top:10px">
            <div style="font-size:11px;font-weight:700;color:${C.muted};margin-bottom:4px">Relaties (${connected.length})</div>
            ${relHtml}
            ${connected.length>15 ? `<div style="font-size:10px;color:${C.muted};margin-top:3px">+${connected.length-15} meer</div>` : ""}
          </div>` : ""}
      </div>`;
  }, [nodes, edges, typeLabels]);

  // When selected changes: update highlight + panel
  useEffect(() => {
    applyHighlight(selected);
    renderPanel(selected);
  }, [selected, applyHighlight, renderPanel]);

  // Init panel on first render - use ref to avoid circular dependency
  const renderPanelRef = useRef(renderPanel);
  useEffect(() => { renderPanelRef.current = renderPanel; }, [renderPanel]);
  useEffect(() => { renderPanelRef.current(null); }, []);

  const applyTransform = useCallback(() => {
    const g = gtRef.current;
    if (g) g.setAttribute("transform", `translate(${pan.current.x},${pan.current.y}) scale(${zoom.current})`);
  }, []);

  const onNodeClick = useCallback((id) => {
    setSelected(prev => prev === id ? null : id);
  }, []);

  const onNodeMouseDown = useCallback((e, id) => { e.stopPropagation(); dragging.current = id; }, []);

  const onSvgMouseMove = useCallback(e => {
    if (dragging.current && posRef.current) {
      const rect = svgRef.current.getBoundingClientRect();
      const pos = posRef.current[dragging.current];
      if (pos) {
        pos.x = (e.clientX - rect.left - pan.current.x) / zoom.current;
        pos.y = (e.clientY - rect.top  - pan.current.y) / zoom.current;
        pos.vx = 0; pos.vy = 0;
        const el = gRef.current?.querySelector(`[data-id="${dragging.current}"]`);
        if (el) el.setAttribute("transform", `translate(${pos.x},${pos.y})`);
      }
    } else if (panStart.current) {
      pan.current.x = panStart.current.px + (e.clientX - panStart.current.mx);
      pan.current.y = panStart.current.py + (e.clientY - panStart.current.my);
      applyTransform();
    }
  // posRef is a ref object (stable) - intentionally omitted from deps
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyTransform]);

  const onSvgMouseUp   = useCallback(() => { dragging.current = null; panStart.current = null; }, []);
  const onSvgMouseDown = useCallback(e => {
    if (e.target === svgRef.current || e.target === gtRef.current)
      panStart.current = {px: pan.current.x, py: pan.current.y, mx: e.clientX, my: e.clientY};
  }, []);

  const onWheel = useCallback(e => {
    e.preventDefault();
    zoom.current = Math.max(0.2, Math.min(3, zoom.current * (e.deltaY < 0 ? 1.1 : 0.9)));
    applyTransform();
  }, [applyTransform]);

  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;
    el.addEventListener("wheel", onWheel, {passive:false});
    return () => el.removeEventListener("wheel", onWheel);
  }, [onWheel]);

  return (
    <div style={{display:"flex", flexDirection:"column", gap:10}}>
      {/* Filter bar */}
      <div style={{display:"flex", alignItems:"center", gap:6, flexWrap:"wrap"}}>
        <span style={{fontSize:12, fontWeight:600, color:C.muted}}>Filter:</span>
        {types.filter(t => nodes.some(n => n.type===t)).map(t => {
          const active = filter.length===0 || filter.includes(t);
          return (
            <button key={t} onClick={() => setFilter(f => f.includes(t) ? f.filter(x=>x!==t) : (f.length===0 ? types.filter(x=>x!==t) : [...f,t]))}
              style={{padding:"3px 9px", borderRadius:20, border:`1.5px solid ${NODE_COLORS[t]}`,
                background:active?NODE_COLORS[t]:"transparent", color:active?C.white:NODE_COLORS[t],
                fontSize:11, fontWeight:600, cursor:"pointer"}}>
              {NODE_ICONS[t]} {typeLabels[t]}
            </button>
          );
        })}
        <button onClick={() => { setSelected(null); setFilter([]); pan.current={x:0,y:0}; zoom.current=1; applyTransform(); }}
          style={{marginLeft:"auto", padding:"3px 9px", borderRadius:20, border:`1px solid ${C.border}`,
            background:C.white, color:C.muted, fontSize:11, cursor:"pointer"}}>↺ Reset</button>
        <span style={{fontSize:11, color:C.muted}}>{nodes.length} nodes · {edges.length} relaties</span>
      </div>

      <div style={{display:"flex", gap:10}}>
        {/* SVG canvas */}
        <div style={{flex:1, background:C.navy, borderRadius:10, overflow:"hidden", position:"relative", minHeight:"calc(100vh - 180px)", cursor:"grab"}}>
          <svg ref={svgRef} width="100%" height="100%" style={{display:"block", minHeight:"calc(100vh - 180px)"}}
            onMouseMove={onSvgMouseMove} onMouseUp={onSvgMouseUp}
            onMouseDown={onSvgMouseDown} onMouseLeave={onSvgMouseUp}>
            <defs>
              {Object.entries(edgeStyle).map(([k,s]) => (
                <marker key={k} id={`arr_${k}`} markerWidth="7" markerHeight="5" refX="6" refY="2.5" orient="auto">
                  <path d="M0,0 L7,2.5 L0,5 Z" fill={s.stroke} opacity="0.7"/>
                </marker>
              ))}
            </defs>
            <g ref={el=>{gtRef.current=el;gRef.current=el;}} transform="translate(0,0) scale(1)">
              {edges.map((e, i) => {
                  const st = edgeStyle[e.type] || edgeStyle.parent;
                  return (
                    <g key={i} data-edge={i} opacity="0.7">
                      <line x1="0" y1="0" x2="0" y2="0" stroke={st.stroke} strokeWidth={st.strokeWidth} strokeDasharray={st.dash} markerEnd={`url(#arr_${e.type})`}/>
                      {e.label && <text x="0" y="0" textAnchor="middle" fill={st.stroke} fontSize="8" fontFamily="monospace" opacity="0.8">{e.label}</text>}
                    </g>
                  );
                })}
                {nodes.map(n => {
                  const r = nodeR[n.type] ?? 16;
                  const col = NODE_COLORS[n.type];
                  const label = n.label?.length > 16 ? n.label.slice(0,14)+"…" : n.label;
                  const isSel = selected === n.id;
                  return (
                    <g key={n.id} data-id={n.id} transform="translate(0,0)"
                      onMouseDown={ev => onNodeMouseDown(ev, n.id)}
                      onClick={() => onNodeClick(n.id)}
                      style={{cursor:"pointer"}}>
                      {isSel && <circle r={r+8} fill="none" stroke={col} strokeWidth="2.5" opacity="0.5"/>}
                      <circle r={r} fill={col} stroke={C.white} strokeWidth={isSel?"2.5":"1.5"} opacity="0.93"/>
                      <text textAnchor="middle" dominantBaseline="central" fontSize={n.type==="og"?13:10} style={{pointerEvents:"none"}}>{NODE_ICONS[n.type]}</text>
                      <text y={r+11} textAnchor="middle" fill={C.white} fontSize="9" fontFamily="system-ui" style={{pointerEvents:"none"}} dominantBaseline="hanging">{label}</text>
                    </g>
                  );
                })}
            </g>
          </svg>
          {/* Zoom buttons */}
          <div style={{position:"absolute", bottom:12, right:12, display:"flex", flexDirection:"column", gap:4}}>
            {[["＋",1.25],["－",0.8]].map(([l,f]) => (
              <button key={l} onClick={() => { zoom.current=Math.max(0.2,Math.min(3,zoom.current*f)); applyTransform(); }}
                style={{width:28, height:28, borderRadius:5, border:"none", background:C.white+"22", color:C.white, fontSize:15, cursor:"pointer", fontWeight:700}}>{l}</button>
            ))}
          </div>
          <div style={{position:"absolute", bottom:12, left:12, fontSize:10, color:C.white, opacity:0.35}}>
            Sleep · Scroll=zoom · <strong style={{color:C.teal}}>Click node</strong> for relations
          </div>
        </div>

        {/* Side panel */}
        <div style={{width:230, flexShrink:0, display:"flex", flexDirection:"column", gap:8}}>
          {/* Legend */}
          <div style={{background:C.white, border:`1px solid ${C.border}`, borderRadius:8, padding:10}}>
            <div style={{fontWeight:700, fontSize:12, color:C.navy, marginBottom:7}}>Legenda</div>
            {types.filter(t => nodes.some(n => n.type===t)).map(t => (
              <div key={t} style={{display:"flex", alignItems:"center", gap:6, marginBottom:4}}>
                <div style={{width:12, height:12, borderRadius:"50%", background:NODE_COLORS[t]}}/>
                <span style={{fontSize:11}}>{NODE_ICONS[t]} {typeLabels[t]}</span>
              </div>
            ))}
            <div style={{marginTop:7, paddingTop:7, borderTop:`1px solid ${C.border}`}}>
              {[{s:"#94A3B8",d:"",l:"OG hiërarchie"},{s:C.blue,d:"5,3",l:"→ Profiel"},{s:C.teal,d:"5,3",l:"→ App"},{s:C.warn,d:"5,3",l:"→ SG"},{s:C.purple,d:"3,3",l:"→ Tag"},{s:"#0891B2",d:"5,3",l:"→ Product"}].map((l,i) => (
                <div key={i} style={{display:"flex", alignItems:"center", gap:5, marginBottom:2}}>
                  <svg width="20" height="8"><line x1="0" y1="4" x2="20" y2="4" stroke={l.s} strokeWidth="1.5" strokeDasharray={l.d}/></svg>
                  <span style={{fontSize:10, color:C.muted}}>{l.l}</span>
                </div>
              ))}
            </div>
          </div>
          {/* Detail panel — updated via DOM */}
          <div ref={panelRef} style={{flex:1}}/>
        </div>
      </div>
    </div>
  );
}


// ── API ───────────────────────────────────────────────────────────────────────
function normUrl(url){return url.replace(/\/+$/,"").replace(/\/API$/i,"");}
function makeHeaders(c){
  return {
    "Authorization":`Basic ${btoa(`${c.username}:${c.password}`)}`,
    "aw-tenant-code":c.apiKey,
    "Accept":"application/json",
    "Content-Type":"application/json"
  };
}
function makeBase(c){return normUrl(c.tenantUrl);}
async function apiGet(c,path){
  const r=await fetch(`${makeBase(c)}/API${path}`,{headers:makeHeaders(c)});
  if(!r.ok)throw new Error(`${r.status} ${r.statusText} — ${path}`);
  return r.json();
}
async function apiGetSafe(c,path){try{return await apiGet(c,path);}catch{return null;}}
async function paginate(c,path,pageSize=500){
  let page=0,results=[];
  while(true){
    const sep=path.includes("?")?"&":"?";
    const data=await apiGet(c,`${path}${sep}page=${page}&pagesize=${pageSize}`);
    const items=data.Profiles??data.LocationGroups??data.SmartGroups??data.Application??data.Products??data.Tags??data.Admins??data.Users??data.UserGroups??data.Devices??[];
    results=results.concat(items);
    if(items.length<pageSize)break;
    page++;
  }
  return results;
}
function buildOGTree(ogs){
  const map={};
  ogs.forEach(og=>{map[og.Id?.Value]={...og,children:[]};});
  const roots=[];
  const assigned=new Set();
  // First pass: assign children
  ogs.forEach(og=>{
    const p=og.ParentLocationGroup?.Id?.Value;
    if(p&&map[p]){
      map[p].children.push(map[og.Id?.Value]);
      assigned.add(og.Id?.Value);
    }
  });
  // Second pass: roots are nodes not assigned as children
  ogs.forEach(og=>{
    if(!assigned.has(og.Id?.Value)) roots.push(map[og.Id?.Value]);
  });
  return roots;
}

// ── Demo ──────────────────────────────────────────────────────────────────────
function makeDemoData(){
  return{
    tenantUrl:"https://demo.awmdm.com",generatedAt:new Date().toISOString(),_demo:true,
    ogs:[
      {Id:{Value:1},Name:"Global",GroupId:"",LocationGroupType:"Global",ParentLocationGroup:null},
      {Id:{Value:3386},Name:"DBO",GroupId:"dwpg-dbo",LocationGroupType:"Customer",ParentLocationGroup:{Id:{Value:1}}},
      {Id:{Value:3387},Name:"Dedicated",GroupId:"DBO-Dedicated",LocationGroupType:"Container",ParentLocationGroup:{Id:{Value:3386}}},
      {Id:{Value:3388},Name:"Shared",GroupId:"DBO-Shared",LocationGroupType:"Container",ParentLocationGroup:{Id:{Value:3386}}},
    ],
    ogTree:[],ogDetails:{},apns:{},
    profiles:[
      {Id:{Value:10},ProfileName:"Android_Passcode",Platform:"Android",OrganizationGroupId:"3386",ProfileStatus:"Active",Type:"Auto"},
      {Id:{Value:11},ProfileName:"iOS-WiFi-Corp",Platform:"Apple iOS",OrganizationGroupId:"3387",ProfileStatus:"Active",Type:"Auto"},
      {Id:{Value:12},ProfileName:"Win10-Restrictions",Platform:"WinRT",OrganizationGroupId:"3388",ProfileStatus:"Active",Type:"Auto"},
    ],
    profileDetails:{},
    apps:[
      {Id:{Value:20},ApplicationName:"Corp Portal",Platform:"Apple iOS",OrganizationGroupId:"3386",AppVersion:"3.1",AppType:"Internal"},
      {Id:{Value:21},ApplicationName:"Microsoft Teams",Platform:"Apple iOS",OrganizationGroupId:"3386",AppType:"Public"},
      {Id:{Value:22},ApplicationName:"Outlook",Platform:"Android",OrganizationGroupId:"3386",AppType:"Public"},
    ],
    smartGroups:[
      {SmartGroupID:40,Name:"All-iOS",CriteriaType:"Device",DeviceCount:45,OrganizationGroupId:"3386"},
      {SmartGroupID:41,Name:"All-Android",CriteriaType:"Device",DeviceCount:23,OrganizationGroupId:"3386"},
    ],
    sgDetails:{40:{Profiles:[{Id:11}],Applications:[{Id:20}]},41:{Profiles:[{Id:10}],Applications:[{Id:22}]}},
    tags:[
      {Id:{Value:1},TagId:1,TagName:"BYOD",TagType:"General",LocationGroupId:"3386"},
      {Id:{Value:2},TagId:2,TagName:"Corporate",TagType:"General",LocationGroupId:"3386"},
    ],
    products:[
      {ID:1,Name:"Deploy-Agent",Platform:"Android",Status:"Active",OrganizationGroupId:"3386"},
    ],
    admins:[{Id:{Value:1},UserName:"doctool",FirstName:"Doc",LastName:"Tool",Email:"admin@example.com",LocationGroup:"DBO"}],
    users:[],userGroups:[],
    eventNotifications:[],
    policies:[],
  };
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function App(){
  const[creds,setCreds]=useState({tenantUrl:"",username:"",password:"",apiKey:"",useProxy:false,startOgId:""});
  const[step,setStep]=useState("login");
  const[log,setLog]=useState([]);
  const[data,setData]=useState({});
  const[activeTab,setTab]=useState("overview");
  const[ogFilter,setOgFilter]=useState(new Set()); // lege set = alles tonen
  const[proxyStatus,setProxyStatus]=useState(null);
  const[testingProxy,setTestingProxy]=useState(false);

  const addLog=(msg,type="info")=>setLog(l=>[...l,{msg,type,ts:new Date().toLocaleTimeString()}]);

  const testProxy=async()=>{
    setTestingProxy(true);setProxyStatus(null);
    try{await fetch("http://localhost:4000",{method:"OPTIONS",signal:AbortSignal.timeout(3000)});setProxyStatus("ok");}
    catch{setProxyStatus("fail");}
    setTestingProxy(false);
  };

  const loadDemo=()=>{
    const d=makeDemoData();d.ogTree=buildOGTree(d.ogs);
    setData(d);setStep("done");setTab("graph");
  };

  const run=useCallback(async()=>{
    setStep("running");setLog([]);setData({});
    const result={};
    try{
      addLog(creds.useProxy?"Via lokale proxy…":"Directe verbinding…");

      // ── 1. OGs ──────────────────────────────────────────────────────────────
      // Haal ALTIJD alle OGs op via globale search.
      // Start OG-ID wordt alleen gebruikt als fallback en als filter-default.
      addLog("Fetching Organization Groups…");
      const startId = creds.startOgId || "3386";
      let allOgs = [];

      // Eerst globale search — geeft alles terug waar account toegang toe heeft
      const ogSearch = await apiGetSafe(creds, "/system/groups/search?pagesize=500");
      allOgs = ogSearch?.LocationGroups ?? ogSearch?.OrganizationGroups ?? [];

      // Als globale search leeg is: haal boom op via Start OG-ID + children
      if (allOgs.length === 0) {
        addLog(`⚠ Globale search leeg, ophalen via OG-ID ${startId}…`, "warn");
        try {
          const root = await apiGet(creds, `/system/groups/${startId}`);
          const childData = await apiGetSafe(creds, `/system/groups/${startId}/children?pagesize=500`);
          const children = childData?.LocationGroups ?? childData?.OrganizationGroups ?? [];
          allOgs = [root, ...children];
        } catch(e2) {
          addLog(`⚠ Fallback mislukt: ${e2.message}`, "warn");
        }
      }

      result.ogs = allOgs;
      result.ogTree = buildOGTree(allOgs);
      addLog(`✓ ${allOgs.length} OGs found`, "success");

      // ── 2. OG details + APNs ───────────────────────────────────────────────
      addLog("Fetching OG details & APNs…");
      result.ogDetails={};result.apns={};
      for(const og of allOgs.slice(0,50)){
        const id=og.Id?.Value;
        const[det,apns]=await Promise.all([
          apiGetSafe(creds,`/system/groups/${id}`),
          apiGetSafe(creds,`/system/groups/${id}/apns`),
        ]);
        if(det){
          result.ogDetails[id]=det;
          // Enrich OG with parent info from detail call if missing in search result
          if(!og.ParentLocationGroup && det.ParentLocationGroup){
            og.ParentLocationGroup = det.ParentLocationGroup;
          }
        }
        if(apns) result.apns[id]=apns;
      }
      // Rebuild tree now that parent info is enriched
      result.ogTree = buildOGTree(result.ogs);
      addLog(`✓ ${Object.keys(result.ogDetails).length} OG details, ${Object.keys(result.apns).length} APNs configs`,"success");

      // ── 3. Tags ────────────────────────────────────────────────────────────
      addLog("Fetching tags…");
      result.tags=[];
      for(const og of allOgs.slice(0,20)){
        const t=await apiGetSafe(creds,`/system/groups/${og.Id?.Value}/tags`);
        if(t?.Tags)result.tags.push(...t.Tags.map(tag=>({...tag,LocationGroupId:og.Id?.Value,LocationGroupName:og.Name})));
      }
      // ook via mdm/tags
      const mdmTags=await apiGetSafe(creds,`/mdm/tags/search?organizationgroupid=${startId}`);
      if(mdmTags?.Tags)mdmTags.Tags.forEach(t=>{if(!result.tags.find(x=>x.TagId===t.TagId))result.tags.push(t);});
      addLog(`✓ ${result.tags.length} tags`,"success");

      // ── 4. Admins ─────────────────────────────────────────────────────────
      addLog("Fetching admins…");
      try{const a=await paginate(creds,"/system/admins/search");result.admins=a;}catch{result.admins=[];addLog("⚠ Admins niet beschikbaar","warn");}
      addLog(`✓ ${result.admins?.length} admins`,"success");

      // ── 5. Users & User Groups ────────────────────────────────────────────
      addLog("Fetching users & user groups…");
      try{result.users=await paginate(creds,"/system/users/search");}catch{result.users=[];}
      try{result.userGroups=await paginate(creds,"/system/usergroups/search");}catch{result.userGroups=[];}
      addLog(`✓ ${result.users?.length} users, ${result.userGroups?.length} user groups`,"success");

      // ── 6. Profiles ──────────────────────────────────────────────────────
      addLog("Profiles ophalen…");
      result.profiles=await paginate(creds,"/mdm/profiles/search");
      result.profiles=result.profiles.map(p=>({...p,Status:p.ProfileStatus??p.Status}));
      addLog(`✓ ${result.profiles.length} profielen`,"success");

      addLog("Fetching profile payload details…");
      result.profileDetails={};
      for(const p of result.profiles.slice(0,150)){
        const det=await apiGetSafe(creds,`/v2/mdm/profiles/${p.Id?.Value}`);
        if(det)result.profileDetails[p.Id?.Value]=det;
      }
      addLog(`✓ ${Object.keys(result.profileDetails).length} profile details`,"success");

      // ── 7. Apps ───────────────────────────────────────────────────────────
      addLog("Fetching apps…");
      result.apps=[];
      try{
        const r=await apiGet(creds,"/mam/apps/search?type=App&page=0&pagesize=500");
        result.apps=r.Application??r.Apps??[];
      }catch{
        try{
          const r=await apiGet(creds,"/mam/apps/search?page=0&pagesize=500");
          result.apps=r.Application??r.Apps??[];
        }catch(e2){addLog(`⚠ Apps: ${e2.message}`,"warn");}
      }
      addLog(`✓ ${result.apps.length} apps`,"success");

      // ── 8. Smart Groups ───────────────────────────────────────────────────
      addLog("Fetching smart groups…");
      try{
        const sgData=await apiGet(creds,"/mdm/smartgroups/search?pagesize=500");
        result.smartGroups=sgData.SmartGroups??sgData.SmartGroup??[];
        result.sgDetails={};
        for(const sg of result.smartGroups.slice(0,150)){
          const det=await apiGetSafe(creds,`/mdm/smartgroups/${sg.SmartGroupID}`);
          // Store detail OR fall back to the search result itself (same structure)
          result.sgDetails[sg.SmartGroupID]=det||sg;
        }
        addLog(`✓ ${result.smartGroups.length} Smart Groups`,"success");
      }catch(e){result.smartGroups=[];addLog(`⚠ Smart Groups: ${e.message}`,"warn");}

      // ── 9. Products / Provisioning ────────────────────────────────────────
      addLog("Fetching products…");
      try{
        const pd=await apiGet(creds,"/mdm/products/search?pagesize=500");
        result.products=pd.Products??pd.Product??[];
        result.productDetails={};
        for(const p of result.products.slice(0,50)){
          const det=await apiGetSafe(creds,`/mdm/products/${p.ID}`);
          if(det)result.productDetails[p.ID]=det;
        }
        addLog(`✓ ${result.products.length} products`,"success");
      }catch(e){result.products=[];addLog(`⚠ Products: ${e.message}`,"warn");}

      // ── 10. Event Notifications ───────────────────────────────────────────
      addLog("Fetching event notifications…");
      try{
        const en=await apiGet(creds,"/system/eventnotifications/search");
        result.eventNotifications=en.EventNotifications??en.Notifications??en??[];
      }catch{result.eventNotifications=[];}
      addLog(`✓ Event notifications`,"success");

      // ── 11. Compliance Policies (via /mdm/compliancepolicies) ────────────
      addLog("Fetching compliance policies…");
      try{
        const cp=await apiGet(creds,"/mdm/compliancepolicies?pagesize=500");
        result.policies=cp.compliancePolicy??cp.CompliancePolicies??cp.Policies??[];
      }catch{
        try{
          const cp2=await apiGet(creds,"/mdm/compliance/policies/search?pagesize=500");
          result.policies=cp2.CompliancePolicies??cp2.Policies??[];
        }catch{result.policies=[];addLog("⚠ Compliance policies niet beschikbaar","warn");}
      }
      addLog(`✓ ${result.policies?.length} compliance policies`,"success");

      // ── 12. LDAP / Directory Services (System API V2) ────────────────────
      addLog("Fetching LDAP configuration…");
      result.ldapConfigs={};
      for(const og of allOgs.slice(0,20)){
        const ldap=await apiGetSafe(creds,`/v2/system/groups/${og.Uuid}/directory-services/ldap-configurations`);
        if(ldap&&ldap.uuid) result.ldapConfigs[og.Id?.Value]=ldap;
      }
      addLog(`✓ ${Object.keys(result.ldapConfigs).length} LDAP configs`,"success");

      // ── 13. DEP Profiles ─────────────────────────────────────────────────
      addLog("Fetching DEP profiles…");
      try{
        const dep=await apiGet(creds,"/mdm/dep/profiles/search?pagesize=500");
        result.depProfiles=dep.ProfileList??dep.Profiles??[];
      }catch{result.depProfiles=[];addLog("⚠ DEP profiles niet beschikbaar","warn");}
      addLog(`✓ ${result.depProfiles?.length} DEP profiles`,"success");

      // ── 14. Scripts ───────────────────────────────────────────────────────
      addLog("Fetching scripts…");
      result.scripts=[];
      for(const og of allOgs.slice(0,20)){
        if(!og.Uuid) continue;
        const s=await apiGetSafe(creds,`/v1/mdm/groups/${og.Uuid}/scripts`);
        if(s?.SearchResults?.length){
          s.SearchResults.forEach(sc=>{ if(!result.scripts.find(x=>x.script_uuid===sc.script_uuid)) result.scripts.push({...sc,ogName:og.Name,ogId:og.Id?.Value}); });
        }
      }
      // Fallback via /api/mdm path
      if(result.scripts.length===0){
        for(const og of allOgs.slice(0,10)){
          if(!og.Uuid) continue;
          const s=await apiGetSafe(creds,`/mdm/groups/${og.Uuid}/scripts`);
          if(s?.SearchResults?.length){
            s.SearchResults.forEach(sc=>{ if(!result.scripts.find(x=>x.script_uuid===sc.script_uuid)) result.scripts.push({...sc,ogName:og.Name,ogId:og.Id?.Value}); });
          }
        }
      }
      addLog(`✓ ${result.scripts.length} scripts`,"success");

      // ── 15. Sensors ───────────────────────────────────────────────────────
      addLog("Fetching sensors…");
      result.sensors=[];
      for(const og of allOgs.slice(0,20)){
        if(!og.Uuid) continue;
        const s=await apiGetSafe(creds,`/v1/mdm/devicesensors/list/${og.Uuid}`);
        if(s?.result_set?.length){
          s.result_set.forEach(se=>{ if(!result.sensors.find(x=>x.uuid===se.uuid)) result.sensors.push({...se,ogName:og.Name,ogId:og.Id?.Value}); });
        }
      }
      if(result.sensors.length===0){
        for(const og of allOgs.slice(0,10)){
          if(!og.Uuid) continue;
          const s=await apiGetSafe(creds,`/mdm/devicesensors/list/${og.Uuid}`);
          if(s?.result_set?.length){
            s.result_set.forEach(se=>{ if(!result.sensors.find(x=>x.uuid===se.uuid)) result.sensors.push({...se,ogName:og.Name,ogId:og.Id?.Value}); });
          }
        }
      }
      addLog(`✓ ${result.sensors.length} sensors`,"success");

      // ── 16. Certificate Authorities ───────────────────────────────────────
      addLog("Fetching certificate authorities…");
      try{
        const ca=await apiGet(creds,`/mdm/picklists/organizationgroups/${startId}/certificateauthorities`);
        result.certificateAuthorities=Array.isArray(ca)?ca:(ca.CertificateAuthorities??[]);
        // Per CA ook templates ophalen
        result.caTemplates={};
        for(const authority of result.certificateAuthorities.slice(0,10)){
          const caId=authority.Key??authority.Id;
          if(!caId) continue;
          const tmpl=await apiGetSafe(creds,`/mdm/picklists/organizationgroups/${startId}/certificateauthorities/${caId}/certificatetemplates`);
          if(tmpl) result.caTemplates[caId]=tmpl;
        }
      }catch{result.certificateAuthorities=[];addLog("⚠ CA niet beschikbaar","warn");}
      addLog(`✓ ${result.certificateAuthorities?.length} Certificate Authorities`,"success");

      // ── 17. Content Categories (MCM) ──────────────────────────────────────
      addLog("Fetching content categories…");
      try{
        const cats=await apiGet(creds,"/mcm/categories");
        result.contentCategories=cats??[];
      }catch{result.contentCategories=[];}
      addLog(`✓ Content categories`,"success");

      // ── 24. Workspace ONE Access / VIDM ──────────────────────────────────
      addLog("Fetching WS1 Access / VIDM configuration…");
      try{
        const vidm=await apiGet(creds,`/system/systemcode/authenticationsource?organizationgroupid=${startId}`);
        result.vidmConfig=vidm;
        addLog(`✓ WS1 Access: ${vidm.isVidmConfigured?"Configured":"Not configured"}`,"success");
      }catch{result.vidmConfig=null;addLog("⚠ WS1 Access config not available","warn");}

      // ── 25. Storage capacity per OG ───────────────────────────────────────
      addLog("Fetching storage capacity…");
      result.storageInfo={};
      for(const og of allOgs.slice(0,20)){
        const s=await apiGetSafe(creds,`/system/groups/${og.Id?.Value}/storage`);
        if(s&&s.ApplicationCapacity) result.storageInfo[og.Id?.Value]=s;
      }
      addLog(`✓ ${Object.keys(result.storageInfo).length} storage configs fetched`,"success");

      result.generatedAt=new Date().toISOString();
      result.tenantUrl=normUrl(creds.tenantUrl);
      setData(result);setStep("done");setTab("graph");
      addLog("✅ As-Built documentation complete!","success");
    }catch(e){addLog(`❌ ${e.message}`,"error");setStep("error");}
  },[creds]);

  // ── Export HTML ────────────────────────────────────────────────────────────
  const exportHTML=()=>{
    const ts=new Date(data.generatedAt).toLocaleString("nl-NL");
    const host=data._demo?"demo":(()=>{try{return new URL(data.tenantUrl).hostname;}catch{return"tenant";}})();

    // ── Helpers ────────────────────────────────────────────────────────────
    const esc = s => String(s??"").replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;");
    const badgeHtml = (txt,color="#1A56A0") => `<span style="background:${color}18;color:${color};border:1px solid ${color}40;border-radius:4px;padding:1px 7px;font-size:11px;font-weight:600;margin:2px">${esc(txt)}</span>`;
    // Filter internal/blob ID fields for clean display
    const SKIP_KEYS = /^(Id$|id$|UUID$|uuid$|Uuid$|.*BlobId|.*blob|.*Blob|.*CertId|.*certid|.*InternalId|.*internal_id|.*_id$|seq$|Seq$|.*Sequence$)$/i;
    const cleanObj = (obj) => {
      if(!obj||typeof obj!=="object") return obj;
      return Object.fromEntries(
        Object.entries(obj).filter(([k,v])=>
          !SKIP_KEYS.test(k) && v!==null && v!==undefined && v!=="" && v!==0 && v!=="0"
        )
      );
    };
    const toRows = (obj,depth=0) => {
      if(!obj||typeof obj!=="object") return "";
      if(Array.isArray(obj)) return obj.map((item,i)=>`
        <tr><td colspan="2" style="padding:0;border:none">
          <table style="width:100%;border-collapse:collapse;font-size:12px;margin:2px 0;background:#FAFBFD">
            ${toRows(item,depth+1)}
          </table>
        </td></tr>
        <tr><td colspan="2" style="border:none;height:4px"></td></tr>`).join("");
      return Object.entries(obj).filter(([,v])=>v!=null&&v!=="").map(([k,v])=>`
        <tr>
          <td style="padding:4px 10px;font-weight:600;color:#5A6A85;width:35%;vertical-align:top;white-space:nowrap;border:1px solid #E8EDF5">${esc(k)}</td>
          <td style="padding:4px 10px;word-break:break-all;border:1px solid #E8EDF5">${typeof v==="object"&&depth<3?`<table style="width:100%;border-collapse:collapse">${toRows(v,depth+1)}</table>`:esc(String(v))}</td>
        </tr>`).join("");
    };
    const section = (title,content,color="#1A56A0") => `
      <div style="background:white;border:1px solid #D1DAEA;border-radius:8px;margin-bottom:16px;overflow:hidden">
        <div style="padding:10px 16px;background:#F4F7FB;border-bottom:1px solid #D1DAEA;border-left:4px solid ${color}">
          <strong style="color:#0F1F3D;font-size:14px">${title}</strong>
        </div>
        <div style="padding:16px">${content}</div>
      </div>`;
    const table = (headers,rows) => `
      <table style="width:100%;border-collapse:collapse;font-size:13px;margin-bottom:4px">
        <thead><tr>${headers.map(h=>`<th style="padding:8px 10px;background:#0F1F3D;color:white;text-align:left;font-weight:600">${h}</th>`).join("")}</tr></thead>
        <tbody>${rows}</tbody>
      </table>`;

    // ── Relation Graph als SVG ────────────────────────────────────────────────
    const buildGraphSVG = () => {
      const {nodes,edges} = buildGraph(data,[]);
      const W=1200, H=800;
      const NC={"og":"#0F1F3D","profile":"#1A56A0","app":"#00A8A8","smartgroup":"#D97706","tag":"#7C3AED","product":"#0891B2","policy":"#16A34A","user":"#BE185D"};
      const NI={"og":"🏢","profile":"📋","app":"📦","smartgroup":"👥","tag":"🏷","product":"⚙","policy":"🛡","user":"👤"};
      const NR={"og":24,"profile":16,"app":16,"smartgroup":18,"tag":13,"product":15,"policy":15};

      // ── Hierarchical layout ────────────────────────────────────────────────
      // OGs: build tree and lay out top-to-bottom
      // Other types: arrange in dedicated horizontal bands below OGs
      const pos={};

      // 1. OG tree layout
      const ogNodes=nodes.filter(n=>n.type==="og");
      const ogChildren={};
      const ogParents=new Set();
      edges.filter(e=>e.type==="parent").forEach(e=>{
        (ogChildren[e.source]||(ogChildren[e.source]=[])).push(e.target);
        ogParents.add(e.target);
      });
      const ogRoots=ogNodes.filter(n=>!ogParents.has(n.id));

      // Assign x positions via BFS
      const ogLevels={};
      const assignLevel=(id,level)=>{
        ogLevels[id]=level;
        (ogChildren[id]||[]).forEach(c=>assignLevel(c,level+1));
      };
      ogRoots.forEach(n=>assignLevel(n.id,0));
      const maxLevel=Math.max(...Object.values(ogLevels),0);
      const levelNodes={};
      Object.entries(ogLevels).forEach(([id,lv])=>{(levelNodes[lv]||(levelNodes[lv]=[])).push(id);});
      const ogH=Math.min(180, H*0.5/(maxLevel+1));
      Object.entries(levelNodes).forEach(([lv,ids])=>{
        const y=40+parseInt(lv)*ogH;
        ids.forEach((id,i)=>{
          pos[id]={x:60+i*(W-80)/Math.max(ids.length,1),y};
        });
      });
      const ogBottom=40+(maxLevel)*ogH+50;

      // 2. Other types in horizontal bands
      const typeOrder=["profile","smartgroup","app","tag","product","policy","user"];
      const bandH=Math.min(100,(H-ogBottom-40)/Math.max(typeOrder.length,1));
      typeOrder.forEach((t,ti)=>{
        const tnodes=nodes.filter(n=>n.type===t);
        if(!tnodes.length)return;
        const y=ogBottom+ti*bandH+bandH/2;
        tnodes.forEach((n,i)=>{
          pos[n.id]={x:40+i*(W-80)/Math.max(tnodes.length,1),y};
        });
      });
      const edgeColors={"parent":"#94A3B8","has_profile":"#1A56A0","has_app":"#00A8A8","has_sg":"#D97706","has_tag":"#7C3AED","has_product":"#0891B2","sg_profile":"#1A56A0","sg_app":"#00A8A8","sg_exclude":"#DC2626"};
      const svgEdges=edges.map((e,i)=>{
        const s=pos[e.source],t=pos[e.target];
        if(!s||!t)return"";
        const col=edgeColors[e.type]||"#94A3B8";
        const dash=e.type==="parent"?"":"5,3";
        return`<line x1="${s.x}" y1="${s.y}" x2="${t.x}" y2="${t.y}" stroke="${col}" stroke-width="1" stroke-dasharray="${dash}" opacity="0.6"/>`;
      }).join("");
      const svgNodes=nodes.map(n=>{
        const p=pos[n.id];if(!p)return"";
        const r=NR[n.type]??14,col=NC[n.type]||"#666";
        const lbl=(n.label?.length>14?n.label.slice(0,12)+"…":n.label)||"";
        return`<g transform="translate(${p.x},${p.y})">
          <circle r="${r}" fill="${col}" stroke="white" stroke-width="1.5" opacity="0.93"/>
          <text text-anchor="middle" dominant-baseline="central" font-size="${n.type==="og"?12:9}">${NI[n.type]||"•"}</text>
          <text y="${r+10}" text-anchor="middle" fill="white" font-size="8" font-family="system-ui">${esc(lbl)}</text>
        </g>`;
      }).join("");
      // Legend
      const legendItems=Object.keys(NC).filter(t=>nodes.some(n=>n.type===t));
      const legend=legendItems.map((t,i)=>`
        <g transform="translate(${10+i*140},${H-25})">
          <circle r="6" fill="${NC[t]}" opacity="0.9"/>
          <text x="10" y="4" font-size="10" fill="#1A2340" font-family="system-ui">${NI[t]||""} ${t}</text>
        </g>`).join("");
      return`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" style="width:100%;background:#0F1F3D;border-radius:8px">
        <rect width="${W}" height="${H}" fill="#0F1F3D" rx="8"/>
        ${svgEdges}
        ${svgNodes}
        <g>${legend}</g>
      </svg>`;
    };

    const html=`<!DOCTYPE html>
<html lang="nl">
<head>
<meta charset="UTF-8">
<title>As-Built — ${esc(data.tenantUrl)}</title>
<style>
*{box-sizing:border-box}
body{font-family:system-ui,-apple-system,sans-serif;margin:0;padding:0;background:#F4F7FB;color:#1A2340}
.page{max-width:1200px;margin:0 auto;padding:32px}
h1{color:#0F1F3D;border-bottom:3px solid #00A8A8;padding-bottom:12px;margin-bottom:24px}
h2{color:#1A56A0;border-bottom:2px solid #E8EDF5;padding-bottom:6px;margin-top:40px;margin-bottom:16px}
h3{color:#0F1F3D;margin-top:24px;margin-bottom:8px;font-size:15px}
h4{color:#1A56A0;margin:12px 0 6px;font-size:13px}
table{width:100%;border-collapse:collapse;font-size:13px;background:white}
td,th{padding:7px 10px;border:1px solid #D1DAEA;text-align:left;vertical-align:top}
th{background:#0F1F3D;color:white;font-weight:600}
tr:nth-child(even) td{background:#F9FAFB}
.badge{display:inline-block;background:#1A56A018;color:#1A56A0;border:1px solid #1A56A040;border-radius:4px;padding:1px 8px;font-size:11px;font-weight:600;margin:2px}
.section{background:white;border:1px solid #D1DAEA;border-radius:8px;padding:20px;margin-bottom:16px}
.card{background:white;border:1px solid #D1DAEA;border-radius:8px;padding:16px;margin-bottom:12px}
.card-header{display:flex;align-items:center;gap:10px;margin-bottom:12px;padding-bottom:10px;border-bottom:1px solid #E8EDF5}
.warn{background:#FFF3CD;border:1px solid #F59E0B;border-radius:6px;padding:12px 16px;margin:16px 0;font-size:13px}
.stat-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(140px,1fr));gap:12px;margin-bottom:24px}
.stat-card{background:white;border:1px solid #D1DAEA;border-radius:8px;padding:16px;border-top:3px solid var(--color)}
.stat-card .num{font-size:28px;font-weight:800;color:var(--color)}
.stat-card .lbl{font-size:12px;color:#5A6A85;margin-top:2px}
details summary{cursor:pointer;font-weight:600;color:#1A56A0;padding:6px 0}
details[open] summary{margin-bottom:8px}
.og-tree{font-family:monospace;line-height:2;font-size:13px}
.toc{background:white;border:1px solid #D1DAEA;border-radius:8px;padding:20px;margin-bottom:32px}
.toc a{color:#1A56A0;text-decoration:none;display:block;padding:3px 0;font-size:13px}
.toc a:hover{text-decoration:underline}
@media print{body{background:white}.page{padding:16px}h2{page-break-before:always}}
</style>
</head>
<body>
<div class="page">

<h1>🏢 As-Built Documentatie — Omnissa UEM</h1>

<!-- Inhoudsopgave -->
<div class="toc">
  <strong style="display:block;margin-bottom:8px;font-size:15px">📑 Inhoudsopgave</strong>
  <a href="#samenvatting">📊 Samenvatting</a>
  <a href="#graph">🔗 Relation Graph</a>
  <a href="#ogs">🏢 Organization Groups</a>
  <a href="#settings">⚙️ Settings per OG</a>
  <a href="#profielen">📋 Profiles</a>
  <a href="#apps">📦 Applicaties</a>
  <a href="#smartgroups">👥 Smart Groups</a>
  <a href="#tags">🏷️ Tags</a>
  <a href="#products">⚙️ Products</a>
  <a href="#scripts">📜 Scripts</a>
  <a href="#sensors">📡 Sensors</a>
  <a href="#compliance">🛡️ Compliance Policies</a>
  <a href="#dep">🍎 DEP Profiles</a>
  <a href="#admins">👤 Admins & Users</a>
  <a href="#ca">🔐 Certificate Authorities</a>
  <a href="#handmatig">📝 Manual te documenteren</a>
</div>

<!-- Samenvatting -->
<h2 id="samenvatting">📊 Samenvatting</h2>
<div class="section">
  <table style="margin-bottom:16px">
    <tr><th colspan="2">Tenant Informatie</th></tr>
    <tr><td><strong>Tenant URL</strong></td><td>${esc(data.tenantUrl)}</td></tr>
    <tr><td><strong>UEM Version</strong></td><td>26.4 (2604)</td></tr>
    <tr><td><strong>Gedocumenteerd op</strong></td><td>${ts}${data._demo?" <em>(demo data)</em>":""}</td></tr>
  </table>
  <div class="stat-grid">
    ${[["OG's",data.ogs?.length??0,"#0F1F3D","🏢"],["Profiles",data.profiles?.length??0,"#1A56A0","📋"],["Apps",data.apps?.length??0,"#00A8A8","📦"],["Smart Groups",data.smartGroups?.length??0,"#D97706","👥"],["Tags",data.tags?.length??0,"#7C3AED","🏷️"],["Products",data.products?.length??0,"#0891B2","⚙️"],["Scripts",data.scripts?.length??0,"#1A56A0","📜"],["Sensors",data.sensors?.length??0,"#00A8A8","📡"],["Compliance",data.policies?.length??0,"#16A34A","🛡️"],["DEP Profiles",data.depProfiles?.length??0,"#555","🍎"],["Admins",data.admins?.length??0,"#BE185D","👤"],["CA's",data.certificateAuthorities?.length??0,"#DC2626","🔐"],["WS1 Access",data.vidmConfig?.isVidmConfigured?"Configured":"Not set","#7C3AED","🔐"]].map(([l,v,c,i])=>`
    <div class="stat-card" style="--color:${c}">
      <div style="font-size:20px">${i}</div>
      <div class="num">${v}</div>
      <div class="lbl">${l}</div>
    </div>`).join("")}
  </div>
</div>

<!-- Relation Graph -->
<h2 id="graph">🔗 Relation Graph</h2>
<div class="section">
  <p style="font-size:12px;color:#5A6A85;margin-bottom:12px">Statische weergave van alle relaties. Klik op de tool voor de interactieve versie.</p>
  ${buildGraphSVG()}
  <div style="display:flex;flex-wrap:wrap;gap:8px;margin-top:12px;font-size:12px">
    ${[["🏢 OG","#0F1F3D"],["📋 Profiel","#1A56A0"],["📦 App","#00A8A8"],["👥 Smart Group","#D97706"],["🏷 Tag","#7C3AED"],["⚙ Product","#0891B2"]].map(([l,c])=>`<span class="badge" style="background:${c}22;color:${c};border-color:${c}44">${l}</span>`).join("")}
  </div>
</div>

<!-- OG Hiërarchie -->
<h2 id="ogs">🏢 Organization Groups (${data.ogs?.length??0})</h2>
${section("OG Overview", table(["Name","Type","Group ID","ID","Parent","Devices","Users"],
  (data.ogs??[]).map(o=>`<tr>
    <td><strong>${esc(o.Name)}</strong></td>
    <td>${esc(o.LocationGroupType??"")}</td>
    <td><code>${esc(o.GroupId??"")}</code></td>
    <td>${esc(o.Id?.Value??"")}</td>
    <td>${esc(o.ParentLocationGroup?.Name??"")}</td>
    <td>${esc(o.Devices??"")}</td>
    <td>${esc(o.Users??"")}</td>
  </tr>`).join("")
))}

<!-- Settings per OG -->
<h2 id="settings">⚙️ Settings per OG</h2>
${Object.keys(data.androidWork||{}).length>0?section("🤖 Android Enterprise (Work)", table(["OG","Enterprise ID","Service Account Email","Admin Email"],
  Object.entries(data.androidWork||{}).map(([ogId,aw])=>{
    const og=(data.ogs??[]).find(o=>o.Id?.Value===parseInt(ogId));
    return`<tr><td><strong>${esc(og?.Name||"OG "+ogId)}</strong></td><td style="font-family:monospace;font-size:11px">${esc(aw.EnterpriseId??"")}</td><td style="font-size:11px">${esc(aw.ServiceAccountEmail??"")}</td><td style="font-size:11px">${esc(aw.ServiceAccountAdminEmail??"")}</td></tr>`;
  }).join("")
)):""}

${Object.keys(data.directEnrollment||{}).length>0?section("📲 Workspace ONE Direct Enrollment", table(["OG","WS1 Direct","Android Ent","Android Legacy","iOS","macOS","Windows"],
  Object.entries(data.directEnrollment||{}).map(([ogId,de])=>{
    const og=(data.ogs??[]).find(o=>o.Id?.Value===parseInt(ogId));
    const yn=v=>`<span class="badge" style="${v?"color:#16A34A":"color:#5A6A85"}">${v?"Yes":"No"}</span>`;
    return`<tr><td><strong>${esc(og?.Name||"OG "+ogId)}</strong></td><td>${yn(de.enableWorkSpaceOneDirectEnrollment)}</td><td>${yn(de.enableWorkSpaceOneDirectEnrollmentForAndroidEnterprise)}</td><td>${yn(de.enableWorkSpaceOneDirectEnrollmentForAndroidLegacy)}</td><td>${yn(de.enableWorkSpaceOneDirectEnrollmentForAppleIos)}</td><td>${yn(de.enableWorkSpaceOneDirectEnrollmentForAppleMacOs)}</td><td>${yn(de.enableWorkSpaceOneDirectEnrollmentForWindows)}</td></tr>`;
  }).join("")
)):""}

${data.vidmConfig?section("🔐 Workspace ONE Access / VIDM Configuration",`<table>${toRows({"VIDM Server URL":data.vidmConfig.vidmServerUrl,"VIDM Configured":String(data.vidmConfig.isVidmConfigured??""),"GreenBox Enabled":String(data.vidmConfig.isGreenBoxCatalogEnabled??""),"GreenBox Server":data.vidmConfig.greenBoxServerUrl})}</table>`):""}

${Object.keys(data.storageInfo||{}).length>0?section("💾 Storage Capacity", table(["OG","App Capacity (MB)","App Max File (MB)","Content Capacity (MB)","Content Max File (MB)"],
  Object.entries(data.storageInfo||{}).map(([ogId,s])=>{
    const og=(data.ogs??[]).find(o=>o.Id?.Value===parseInt(ogId));
    return`<tr><td><strong>${esc(og?.Name||"OG "+ogId)}</strong></td><td style="text-align:right">${esc(s.ApplicationCapacity)}</td><td style="text-align:right">${esc(s.ApplicationMaxFileSize)}</td><td style="text-align:right">${esc(s.ContentCapacity)}</td><td style="text-align:right">${esc(s.ContentMaxFileSize)}</td></tr>`;
  }).join("")
)):""}

${(data.ogs??[]).map(og=>{
  const ogId=og.Id?.Value;
  const apns=data.apns?.[ogId];
  const ldap=data.ldapConfigs?.[ogId];
  const sr=data.sampleRates?.[ogId];
  const ca=data.customAttributes?.[ogId];
  const acc=data.accStatus?.[ogId];
  if(!apns&&!ldap&&!sr&&!ca&&!acc) return "";
  return `<h3>🏢 ${esc(og.Name)}</h3>
  ${apns?section("📱 APNs",`<table>${toRows({
  "Apple ID": apns.AppleId??apns.AppleID,
  "Certificate Expiry": apns.ExpirationDate??apns.CertificateExpirationDate,
  "Subject": apns.Subject??apns.CertificateSubject,
  "Topic": apns.Topic??apns.CertificateTopic,
  "Status": apns.Status??apns.CertificateStatus,
  "Push Endpoint": apns.PushEndpoint,
})}</table>`,"#00A8A8"):""}
  ${ldap?(()=>{const ld={"Server":ldap.server_config?.host??ldap.ServerConfig?.Host,"Port":ldap.server_config?.port??ldap.ServerConfig?.Port,"Protocol":ldap.server_config?.protocol??ldap.ServerConfig?.Protocol,"Bind DN":ldap.user_config?.bind_dn??ldap.UserConfig?.BindDistinguishedName,"Base DN":ldap.user_config?.base_dn??ldap.UserConfig?.BaseDN};return section("🔗 LDAP / Directory Services","<table>"+toRows(ld)+"</table>","#1A56A0");})():""}
  ${acc?section("☁️ Cloud Connector Status",`<table>${toRows(acc)}</table>`,"#16A34A"):""}
  ${sr?section("📊 Sample Rates",`<table>${toRows(sr)}</table>`,"#D97706"):""}`;
}).join("")}

<!-- Profiles -->
<h2 id="profielen">📋 Profiles (${data.profiles?.length??0})</h2>
${section("Profiel Overview", table(["Name","Platform","OG","Assignment","Status","Scope"],
  (data.profiles??[]).map(p=>`<tr>
    <td><strong>${esc(p.ProfileName??p.Name??"")}</strong></td>
    <td>${esc(p.Platform??"")}</td>
    <td>${esc(p.OrganizationGroupName??p.OrganizationGroupId??"")}</td>
    <td>${esc(p.Type??p.AssignmentType??"")}</td>
    <td><span class="badge" style="${(p.Status??p.ProfileStatus)==="Active"?"background:#16A34A18;color:#16A34A;border-color:#16A34A40":"background:#fee2e218;color:#DC2626;border-color:#fecaca"}">${esc(p.Status??p.ProfileStatus??"")}</span></td>
    <td>${esc(p.ProfileScope??"")}</td>
  </tr>`).join("")
))}

${(data.profiles??[]).map(p=>{
  const det=data.profileDetails?.[p.Id?.Value];
  const gen=det?.General;
  const assignedSGs=gen?.AssignedSmartGroups??[];
  const excludedSGs=gen?.ExcludedSmartGroups??[];
  const payloadKeys=det?Object.keys(det).filter(k=>k!=="General"):[];
  return `
<div class="card">
  <div class="card-header">
    <span style="font-size:20px">${p.Platform?.toLowerCase().includes("android")?"🤖":p.Platform?.toLowerCase().includes("apple")||p.Platform?.toLowerCase().includes("ios")?"🍎":p.Platform?.toLowerCase().includes("win")?"🪟":"📱"}</span>
    <div>
      <strong style="font-size:15px">${esc(p.ProfileName??p.Name??"")}</strong>
      <div style="font-size:12px;color:#5A6A85">${esc(p.Platform??"")} · ${esc(p.OrganizationGroupName??p.OrganizationGroupId??"")}</div>
    </div>
    <span class="badge" style="${(p.Status??p.ProfileStatus)==="Active"?"background:#16A34A18;color:#16A34A;border-color:#16A34A40":"background:#fee2e218;color:#DC2626"}">${esc(p.Status??p.ProfileStatus??"")}</span>
    <span class="badge">${esc(gen?.AssignmentType??p.Type??"")}</span>
  </div>

  <table style="margin-bottom:12px">
    ${toRows({"Allow Removal":gen?.AllowRemoval,"Is Active":gen?.IsActive!=null?String(gen.IsActive):undefined,"Version":gen?.Version,"UUID":gen?.ProfileUuid,"Scope":gen?.ProfileScope??p.ProfileScope})}
  </table>

  ${assignedSGs.length>0?`<div style="margin:8px 0"><strong>👥 Assigned Smart Groups:</strong><br>${assignedSGs.map(sg=>`<span class="badge">${esc(sg.Name??String(sg))}</span>`).join("")}</div>`:""}
  ${excludedSGs.length>0?`<div style="margin:8px 0"><strong style="color:#DC2626">🚫 Excluded Smart Groups:</strong><br>${excludedSGs.map(sg=>`<span class="badge" style="background:#fee2e218;color:#DC2626;border-color:#fecaca">${esc(sg.Name??String(sg))}</span>`).join("")}</div>`:""}

  ${payloadKeys.map(key=>{
    const payload=det[key];
    const items=Array.isArray(payload)?payload:[payload];
    return`<details style="margin-top:8px">
      <summary>⚙️ ${esc(key)} (${items.length} item${items.length!==1?"s":""})</summary>
      ${items.map((item,i)=>typeof item==="object"&&item?`
        <h4>${esc(key)} #${i+1}</h4>
        <table>${toRows(item)}</table>`:`<code>${esc(String(item))}</code>`).join("")}
    </details>`;
  }).join("")}
</div>`;
}).join("")}

<!-- Apps -->
<h2 id="apps">📦 Applicaties (${data.apps?.length??0})</h2>
${section("App Overview", table(["Name","Platform","Type","Version","OG","Assigned","Installed","Status","Smart Groups"],
  (data.apps??[]).map(a=>`<tr>
    <td><strong>${esc(a.ApplicationName??a.AppName??"")}</strong>${a.DisplayName&&a.DisplayName!==a.ApplicationName?`<br><small style="color:#5A6A85">${esc(a.DisplayName)}</small>`:""}</td>
    <td>${esc(PLATFORM_MAP[a.Platform]??String(a.Platform??""))}</td>
    <td>${esc(a.AppType??APP_SOURCE[a.ApplicationSource]??"")}</td>
    <td>${esc(a.AppVersion??"")}</td>
    <td>${esc(a.RootLocationGroupName??a.OrganizationGroupName??"")}</td>
    <td style="text-align:center">${a.AssignedDeviceCount??""}</td>
    <td style="text-align:center">${a.InstalledDeviceCount??""}</td>
    <td><span class="badge" style="${a.Status==="Active"?"color:#16A34A;border-color:#16A34A40":""}">${esc(a.Status??"")}</span></td>
    <td>${(a.SmartGroups??[]).map(sg=>`<span class="badge" style="background:#D9770618;color:#D97706;border-color:#D9770640">${esc(sg.Name??String(sg))}</span>`).join("")}</td>
  </tr>`).join("")
))}

<!-- Smart Groups -->
<h2 id="smartgroups">👥 Smart Groups (${data.smartGroups?.length??0})</h2>
${section("Smart Group Overview", table(["Name","Criteria Type","Devices","OG"],
  (data.smartGroups??[]).map(s=>`<tr>
    <td><strong>${esc(s.Name)}</strong></td>
    <td>${esc(s.CriteriaType??"")}</td>
    <td style="text-align:center">${s.DeviceCount??""}</td>
    <td>${esc(s.OrganizationGroupName??"")}</td>
  </tr>`).join("")
))}
${(data.smartGroups??[]).map(sg=>{
  const det=data.sgDetails?.[sg.SmartGroupID]||sg;
  // Build criteria list from all non-empty arrays
  const criteriaItems=[
    ...(det.OrganizationGroups||[]).map(o=>`<span class="badge" style="background:#0F1F3D18;color:#0F1F3D">🏢 ${esc(o.Name||o)}</span>`),
    ...(det.UserGroups||[]).map(g=>`<span class="badge" style="background:#BE185D18;color:#BE185D">👤 ${esc(g.Name||g)}</span>`),
    ...(det.Tags||[]).map(t=>`<span class="badge" style="background:#7C3AED18;color:#7C3AED">🏷 ${esc(t.Name||t)}</span>`),
    ...(det.Ownerships||[]).map(o=>`<span class="badge" style="background:#D9770618;color:#D97706">📋 ${esc(o)}</span>`),
    ...(det.Platforms||[]).map(p=>`<span class="badge" style="background:#1A56A018;color:#1A56A0">📱 ${esc(p.Name||p)}</span>`),
    ...(det.Models||[]).map(m=>`<span class="badge" style="background:#0891B218;color:#0891B2">📲 ${esc(m.Name||m.ModelName||m)}</span>`),
    ...(det.OperatingSystems||[]).map(o=>`<span class="badge" style="background:#16A34A18;color:#16A34A">💻 ${esc(o.Product||o.Name||o)}</span>`),
    ...(det.UserAdditions||[]).map(u=>`<span class="badge" style="background:#16A34A18;color:#16A34A">👤+ ${esc(u.Name||u.UserName||u)}</span>`),
    ...(det.DeviceAdditions||[]).map(d=>`<span class="badge" style="background:#16A34A18;color:#16A34A">📱+ ${esc(d.Name||d.FriendlyName||d)}</span>`),
    ...(det.UserExclusions||[]).map(u=>`<span class="badge" style="background:#DC262618;color:#DC2626">👤- ${esc(u.Name||u.UserName||u)}</span>`),
    ...(det.DeviceExclusions||[]).map(d=>`<span class="badge" style="background:#DC262618;color:#DC2626">📱- ${esc(d.Name||d.FriendlyName||d)}</span>`),
    ...(det.UserGroupExclusions||[]).map(g=>`<span class="badge" style="background:#DC262618;color:#DC2626">👥- ${esc(g.Name||g)}</span>`),
    ...(det.ManagementTypes||[]).map(m=>`<span class="badge">Mgmt: ${esc(m)}</span>`),
    ...(det.EnrollmentCategories||[]).map(e=>`<span class="badge">Enroll: ${esc(e.Name||e)}</span>`),
    ...(det.OEMAndModels||[]).map(o=>`<span class="badge">OEM: ${esc(o.OEM||o)}</span>`),
  ];
  // Find assigned profiles from profileDetails
  const assignedProfiles=(data.profiles||[]).filter(p=>{
    const pd=data.profileDetails?.[p.Id?.Value];
    return pd?.General?.AssignedSmartGroups?.some(s=>s.SmartGroupId===sg.SmartGroupID||s.SmartGroupId===sg.SmartGroupUuid);
  });
  // Find assigned apps
  const assignedApps=(data.apps||[]).filter(a=>(a.SmartGroups||[]).some(s=>s.Id===sg.SmartGroupID));

  return`
<div class="card" style="margin-bottom:12px">
  <div class="card-header">
    <span style="font-size:18px">👥</span>
    <strong style="font-size:15px">${esc(sg.Name)}</strong>
    <span class="badge">${esc(sg.CriteriaType??"")}</span>
    <span class="badge" style="background:#1A56A018;color:#1A56A0">📱 ${sg.Devices??0} devices</span>
    ${sg.Assignments?`<span class="badge" style="background:#16A34A18;color:#16A34A">📋 ${sg.Assignments} assigned</span>`:""}
    ${sg.Exclusions?`<span class="badge" style="background:#DC262618;color:#DC2626">🚫 ${sg.Exclusions} excluded</span>`:""}
  </div>
  <table style="margin-bottom:10px">
    ${toRows({"Managed by OG":sg.ManagedByOrganizationGroupName??sg.OrganizationGroupName,"UUID":sg.SmartGroupUuid})}
  </table>
  ${criteriaItems.length>0?`
  <div style="margin:8px 0">
    <strong style="font-size:12px;color:#5A6A85">Criteria:</strong><br>
    <div style="margin-top:4px">${criteriaItems.join(" ")}</div>
  </div>`:"<p style='color:#5A6A85;font-size:12px'>No specific criteria (CriteriaType: "+esc(sg.CriteriaType||"")+")</p>"}
  ${assignedProfiles.length>0?`
  <div style="margin-top:10px">
    <strong style="font-size:12px;color:#1A56A0">📋 Assigned Profiles (${assignedProfiles.length}):</strong><br>
    ${assignedProfiles.map(p=>`<span class="badge">${esc(p.ProfileName??p.Name??"")}</span>`).join("")}
  </div>`:""}
  ${assignedApps.length>0?`
  <div style="margin-top:8px">
    <strong style="font-size:12px;color:#00A8A8">📦 Assigned Apps (${assignedApps.length}):</strong><br>
    ${assignedApps.map(a=>`<span class="badge" style="background:#00A8A818;color:#00A8A8;border-color:#00A8A840">${esc(a.ApplicationName??a.AppName??"")}</span>`).join("")}
  </div>`:""}
</div>`;
}).join("")}

<!-- Tags -->
<h2 id="tags">🏷️ Tags (${data.tags?.length??0})</h2>
${section("Tags", table(["Tag Name","Type","OG","ID"],
  (data.tags??[]).map(t=>`<tr>
    <td><strong>${esc(t.TagName)}</strong></td>
    <td>${esc(t.TagType??"")}</td>
    <td>${esc(t.LocationGroupName??t.LocationGroupId??"")}</td>
    <td style="color:#5A6A85;font-family:monospace">${t.TagId??t.Id?.Value??""}</td>
  </tr>`).join("")
))}

<!-- Products -->
<h2 id="products">⚙️ Products / Provisioning (${data.products?.length??0})</h2>
${section("Products", table(["Name","Platform","Status","OG"],
  (data.products??[]).map(p=>`<tr>
    <td><strong>${esc(p.Name??"")}</strong></td>
    <td>${esc(p.Platform??"")}</td>
    <td><span class="badge" style="${p.Status==="Active"?"color:#16A34A":""}">${esc(p.Status??"")}</span></td>
    <td>${esc(p.OrganizationGroupName??"")}</td>
  </tr>`).join("")
))}

<!-- Scripts -->
<h2 id="scripts">📜 Scripts (${data.scripts?.length??0})</h2>
${data.scripts?.length?section("Scripts", table(["Name","Platform","Type","OG","UUID"],
  (data.scripts??[]).map(s=>`<tr>
    <td><strong>${esc(s.name??s.Name??"")}</strong>${s.description?`<br><small style="color:#5A6A85">${esc(s.description)}</small>`:""}</td>
    <td>${esc(s.platform??s.Platform??"")}</td>
    <td>${esc(s.script_type??s.type??"")}</td>
    <td>${esc(s.ogName??s.organization_group_name??"")}</td>
    <td style="font-family:monospace;font-size:11px">${esc(s.script_uuid??s.uuid??"")}</td>
  </tr>`).join("")
)):"<div class='section' style='color:#5A6A85'>Geen scripts found.</div>"}

<!-- Sensors -->
<h2 id="sensors">📡 Sensors (${data.sensors?.length??0})</h2>
${data.sensors?.length?section("Sensors", table(["Name","Platform","Type","OG","UUID"],
  (data.sensors??[]).map(s=>`<tr>
    <td><strong>${esc(s.name??s.Name??"")}</strong>${s.description?`<br><small>${esc(s.description)}</small>`:""}</td>
    <td>${esc(s.platform??s.Platform??"")}</td>
    <td>${esc(s.query_type??s.QueryType??"")}</td>
    <td>${esc(s.ogName??s.organization_group_name??"")}</td>
    <td style="font-family:monospace;font-size:11px">${esc(s.uuid??"")}</td>
  </tr>`).join("")
)):"<div class='section' style='color:#5A6A85'>Geen sensors found.</div>"}

<!-- Compliance Policies -->
<h2 id="compliance">🛡️ Compliance Policies (${data.policies?.length??0})</h2>
${data.policies?.length?`
${section("Compliance Overview", table(["Name","Platform","Actief","ID"],
  (data.policies??[]).map(p=>`<tr>
    <td><strong>${esc(p.name??p.Name??p.PolicyName??"")}</strong>${p.description?`<br><small>${esc(p.description)}</small>`:""}</td>
    <td>${esc(p.platform??p.Platform??"")}</td>
    <td><span class="badge" style="${p.isActive||p.IsActive?"color:#16A34A":"color:#DC2626"}">${p.isActive!=null?String(p.isActive):p.IsActive!=null?String(p.IsActive):""}</span></td>
    <td>${p.devicePolicyId??p.Id?.Value??""}</td>
  </tr>`).join("")
))}
${(data.policies??[]).map(p=>`
<div class="card">
  <div class="card-header">
    <span style="font-size:18px">🛡️</span>
    <strong>${esc(p.name??p.Name??p.PolicyName??"")}</strong>
    <span class="badge" style="${p.isActive||p.IsActive?"color:#16A34A":"color:#DC2626"}">${p.isActive!=null?String(p.isActive):""}</span>
  </div>
  <table>${toRows(p)}</table>
</div>`).join("")}`
:"<div class='section' style='color:#5A6A85'>Geen compliance policies found.</div>"}

<!-- DEP Profiles -->
<h2 id="dep">🍎 DEP / Apple Automated Device Enrollment (${data.depProfiles?.length??0})</h2>
${data.depProfiles?.length?section("DEP Profiles", table(["Name","Department","Supervised","Allow Remove","Support Phone","UUID"],
  (data.depProfiles??[]).map(p=>`<tr>
    <td><strong>${esc(p.ProfileName??p.Name??"")}</strong></td>
    <td>${esc(p.Department??"")}</td>
    <td>${p.IsSupervisedDevice!=null?`<span class="badge" style="${p.IsSupervisedDevice?"color:#16A34A":"color:#5A6A85"}">${p.IsSupervisedDevice?"Ja":"Nee"}</span>`:""}</td>
    <td>${p.AllowRemoveProfile!=null?`<span class="badge">${p.AllowRemoveProfile?"Ja":"Nee"}</span>`:""}</td>
    <td>${esc(p.SupportPhoneNumber??"")}</td>
    <td style="font-family:monospace;font-size:11px">${esc(p.ProfileUuid??p.SharedMdmProfileDataId??"")}</td>
  </tr>`).join("")
)):"<div class='section' style='color:#5A6A85'>Geen DEP profiles found.</div>"}

<!-- Admins -->
<h2 id="admins">👤 Admins & Users (${data.admins?.length??0})</h2>
${section("Administrators", table(["Username","Name","Email","OG","Rol"],
  (data.admins??[]).map(a=>`<tr>
    <td><code>${esc(a.UserName??"")}</code></td>
    <td>${esc((a.FirstName??"")+" "+(a.LastName??""))}</td>
    <td>${esc(a.Email??"")}</td>
    <td>${esc(a.LocationGroup??"")}</td>
    <td>${esc(a.RoleName??a.Role??"")}</td>
  </tr>`).join("")
))}
${data.userGroups?.length?section(`User Groups (${data.userGroups.length})`, table(["Name","Type","Gebruikers","OG"],
  (data.userGroups??[]).map(g=>`<tr>
    <td><strong>${esc(g.UserGroupName??g.Name??"")}</strong></td>
    <td>${esc(g.GroupType??g.Type??"")}</td>
    <td>${g.UserCount??g.UsersCount??""}</td>
    <td>${esc(g.OrganizationGroupName??"")}</td>
  </tr>`).join("")
)):""}

<!-- Certificate Authorities -->
<h2 id="ca">🔐 Certificate Authorities (${data.certificateAuthorities?.length??0})</h2>
${data.certificateAuthorities?.length?`
${(data.certificateAuthorities??[]).map(ca=>{
  const caId=ca.Key??ca.Id;
  const templates=data.caTemplates?.[caId];
  return`<div class="card">
    <div class="card-header"><span style="font-size:18px">🔐</span><strong>${esc(ca.Description??ca.Name??String(caId))}</strong><span class="badge" style="color:#DC2626">CA ID: ${caId}</span></div>
    ${templates&&Array.isArray(templates)&&templates.length?`
    <strong style="font-size:13px">Templates (${templates.length}):</strong>
    ${table(["Name/Description","ID"],templates.map(t=>`<tr><td>${esc(t.Description??t.Name??t.Value??"")}</td><td>${t.Key??t.Id??""}</td></tr>`).join(""))}`:
    "<p style='color:#5A6A85;font-size:13px'>Geen templates found.</p>"}
  </div>`;
}).join("")}`
:"<div class='section' style='color:#5A6A85'>Geen Certificate Authorities found.</div>"}

<!-- Manual -->
<h2 id="handmatig">📝 Manual te documenteren</h2>
<div class="warn">⚠ Onderstaande instellingen zijn niet beschikbaar via de REST API en moeten handmatig worden gedocumenteerd vanuit de UEM Console.</div>
${section("Console-only Settings", table(["Instelling","Pad in Console","✓"],
  [["Enrollment Settings","Groups & Settings → All Settings → Devices & Users → General → Enrollment","☐"],["Enrollment Restrictions","Groups & Settings → All Settings → Devices & Users → General → Enrollment Restrictions","☐"],["Exchange / EAS","Groups & Settings → All Settings → Devices & Users → General → Exchange","☐"],["Passcode Policy","Groups & Settings → All Settings → Devices & Users → General → Passcode","☐"],["Privacy Settings","Groups & Settings → All Settings → Devices & Users → General → Privacy","☐"],["SCEP / CA","Groups & Settings → All Settings → System → Enterprise Integration → Certificate Authorities","☐"],["SMTP","Groups & Settings → All Settings → System → Enterprise Integration → Email","☐"],["Branding","Groups & Settings → All Settings → System → Branding","☐"],["Relay Servers","Groups & Settings → All Settings → System → Enterprise Integration → Relay Servers","☐"],["DEP / ABM","Groups & Settings → All Settings → Devices & Users → Apple → Apple DEP","☐"],["VPP","Groups & Settings → All Settings → Devices & Users → Apple → VPP Managed Distribution","☐"],["Content Gateway","Groups & Settings → All Settings → Content → Content Gateway","☐"],["SSO","Groups & Settings → All Settings → System → Security → SSO","☐"],["Kerberos","Groups & Settings → All Settings → Devices & Users → Advanced → Kerberos","☐"],["WNS","Groups & Settings → All Settings → Devices & Users → Windows → WNS","☐"],["Android EMM","Groups & Settings → All Settings → Devices & Users → Android → Android EMM Registration","☐"]]
  .map(([s,p,c])=>`<tr><td>${s}</td><td><code style="font-size:11px">${p}</code></td><td style="text-align:center;font-size:16px">${c}</td></tr>`).join("")
))}

<footer style="margin-top:48px;padding-top:16px;border-top:2px solid #E8EDF5;text-align:center;font-size:12px;color:#5A6A85">
  <p>© 2026 <a href="https://www.debruinonline.net" style="color:#1A56A0;text-decoration:none;font-weight:600">DeBruinOnline.net</a> · 
  Omnissa UEM As-Built Documentation Tool · 
  Built with <a href="https://claude.ai" style="color:#1A56A0;text-decoration:none">Claude (Anthropic)</a> · 
  MIT License</p>
  <p style="font-size:11px;color:#94A3B8">Generated: ${ts} · UEM ${esc(data.tenantUrl)} · v2604</p>
</footer>

<details style="margin-top:32px">
  <summary><strong>📎 Ruwe JSON export (volledige dataset)</strong></summary>
  <pre style="background:#0F1F3D;color:#E2E8F0;padding:16px;border-radius:6px;overflow:auto;font-size:11px;max-height:400px">${esc(JSON.stringify({ogs:data.ogs,profiles:data.profiles?.map(p=>({...p,detail:data.profileDetails?.[p.Id?.Value]})),apps:data.apps,smartGroups:data.smartGroups,tags:data.tags,policies:data.policies,scripts:data.scripts,sensors:data.sensors,depProfiles:data.depProfiles},null,2))}</pre>
</details>

</div>
</body></html>`;
    const a=document.createElement("a");
    a.href=URL.createObjectURL(new Blob([html],{type:"text/html"}));
    a.download=`as-built-${host}-${new Date().toISOString().slice(0,10)}.html`;
    a.click();
  }


  // ── Login ──────────────────────────────────────────────────────────────────
  if(step==="login")return(
    <div style={{minHeight:"100vh",background:C.navy,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"system-ui,sans-serif",padding:16}}>
      <div style={{background:C.white,borderRadius:12,padding:34,width:"100%",maxWidth:460,boxShadow:"0 24px 60px #00000055"}}>
        <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:22}}>
          <div style={{width:44,height:44,background:C.teal,borderRadius:10,display:"flex",alignItems:"center",justifyContent:"center",fontSize:22}}>🏢</div>
          <div><div style={{fontWeight:800,fontSize:17,color:C.navy}}>Omnissa UEM</div><div style={{fontSize:12,color:C.muted}}>As-Built Documentatietool v2604</div></div>
        </div>
        {[
          {key:"tenantUrl",label:"Tenant URL",placeholder:"https://as2060.awmdm.com",type:"url"},
          {key:"apiKey",label:"API Key (aw-tenant-code)",placeholder:"REST API key",type:"text"},
          {key:"startOgId",label:"Start OG ID (optional, e.g. 3386)",placeholder:"3386",type:"text"},
        ].map(f=>(<div key={f.key} style={{marginBottom:13}}>
          <label style={{display:"block",fontSize:12,fontWeight:600,color:C.muted,marginBottom:4}}>{f.label}</label>
          <input type={f.type} placeholder={f.placeholder} value={creds[f.key]}
            onChange={e=>setCreds(c=>({...c,[f.key]:e.target.value}))}
            style={{width:"100%",boxSizing:"border-box",padding:"9px 12px",border:`1.5px solid ${C.border}`,borderRadius:6,fontSize:13,color:C.text,outline:"none",fontFamily:"monospace"}}/>
        </div>))}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:13}}>
          {[{key:"username",label:"Username",placeholder:"admin",type:"text"},{key:"password",label:"Password",placeholder:"••••••••",type:"password"}].map(f=>(<div key={f.key}>
            <label style={{display:"block",fontSize:12,fontWeight:600,color:C.muted,marginBottom:4}}>{f.label}</label>
            <input type={f.type} placeholder={f.placeholder} value={creds[f.key]}
              onChange={e=>setCreds(c=>({...c,[f.key]:e.target.value}))}
              style={{width:"100%",boxSizing:"border-box",padding:"9px 12px",border:`1.5px solid ${C.border}`,borderRadius:6,fontSize:13,color:C.text,outline:"none"}}/>
          </div>))}
        </div>
        <div style={{background:C.mist,borderRadius:8,padding:"11px 14px",marginBottom:13,border:`1px solid ${C.border}`}}>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <div onClick={()=>{setCreds(c=>({...c,useProxy:!c.useProxy}));setProxyStatus(null);}}
              style={{width:36,height:20,borderRadius:10,background:creds.useProxy?C.teal:C.border,position:"relative",transition:"background 0.2s",cursor:"pointer",flexShrink:0}}>
              <div style={{position:"absolute",top:3,left:creds.useProxy?18:3,width:14,height:14,borderRadius:"50%",background:C.white,transition:"left 0.2s"}}/>
            </div>
            <div style={{flex:1}}>
              <div style={{fontSize:13,fontWeight:600,color:C.navy}}>Use local proxy</div>
              <div style={{fontSize:11,color:C.muted}}>Required for Windows (CORS)</div>
            </div>
            {creds.useProxy&&<button onClick={testProxy} disabled={testingProxy} style={{padding:"4px 9px",borderRadius:5,border:`1px solid ${C.border}`,background:C.white,fontSize:11,cursor:"pointer",color:C.blue,fontWeight:600}}>{testingProxy?"…":"Test"}</button>}
          </div>
          {creds.useProxy&&proxyStatus==="ok"&&<div style={{fontSize:12,color:C.success,marginTop:7}}>✅ Proxy bereikbaar op localhost:4000</div>}
          {creds.useProxy&&proxyStatus==="fail"&&<div style={{fontSize:12,color:C.error,marginTop:7}}>❌ Proxy niet bereikbaar — start <code>node proxy.js</code></div>}
        </div>
        <div style={{fontSize:11,color:C.muted,marginBottom:13,padding:"7px 10px",background:C.mist,borderRadius:5}}>🔒 Credentials worden alleen lokaal gebruikt.</div>
        <button onClick={run} disabled={!creds.tenantUrl||!creds.apiKey||!creds.username||!creds.password}
          style={{width:"100%",padding:"12px",background:(creds.tenantUrl&&creds.apiKey&&creds.username&&creds.password)?C.teal:C.border,color:C.white,border:"none",borderRadius:7,fontWeight:700,fontSize:14,cursor:"pointer",marginBottom:9}}>
          🚀 Start As-Built documentation
        </button>
        <button onClick={loadDemo} style={{width:"100%",padding:"9px",background:"transparent",color:C.blue,border:`1.5px solid ${C.blue}`,borderRadius:7,fontWeight:600,fontSize:13,cursor:"pointer"}}>
          🎭 Demo data
        </button>
        <div style={{marginTop:20,paddingTop:16,borderTop:`1px solid ${C.border}`,textAlign:"center",fontSize:11,color:C.muted}}>
          <div>© 2026 <a href="https://www.debruinonline.net" target="_blank" rel="noreferrer" style={{color:C.blue,textDecoration:"none",fontWeight:600}}>DeBruinOnline.net</a></div>
          <div style={{marginTop:3}}>Built with <a href="https://claude.ai" target="_blank" rel="noreferrer" style={{color:C.blue,textDecoration:"none"}}>Claude (Anthropic)</a> · MIT License</div>
        </div>
      </div>
    </div>
  );

  if(step==="running"||(step==="error"&&!data.ogs))return(
    <div style={{minHeight:"100vh",background:C.navy,fontFamily:"system-ui,sans-serif",padding:40,display:"flex",flexDirection:"column",alignItems:"center"}}>
      <div style={{background:C.white,borderRadius:10,padding:32,width:"100%",maxWidth:620}}>
        <div style={{fontWeight:800,fontSize:18,color:C.navy,marginBottom:18}}>{step==="running"?"⏳ Fetching As-Built data…":"❌ Error occurred"}</div>
        <div style={{background:C.navy,borderRadius:8,padding:14,height:340,overflowY:"auto",fontFamily:"monospace",fontSize:12}}>
          {log.map((l,i)=>(<div key={i} style={{color:l.type==="success"?"#4ADE80":l.type==="error"?"#F87171":l.type==="warn"?"#FCD34D":"#94A3B8",marginBottom:3}}>
            <span style={{color:"#475569"}}>[{l.ts}]</span> {l.msg}
          </div>))}
          {step==="running"&&<div style={{color:C.teal}}>▌</div>}
        </div>
        {step==="error"&&<button onClick={()=>{setStep("login");setLog([]);}} style={{marginTop:12,padding:"9px 20px",background:C.error,color:C.white,border:"none",borderRadius:6,fontWeight:700,cursor:"pointer"}}>← Terug</button>}
      </div>
    </div>
  );

  // ── Done ───────────────────────────────────────────────────────────────────

  // OG filter helpers
  const ogFilterActive = ogFilter.size > 0;
  const ogIds = ogFilterActive ? ogFilter : new Set((data.ogs??[]).map(o=>o.Id?.Value));

  // Filter een lijst objecten op OG-id veld
  const filterByOG = (list, idField="OrganizationGroupId") =>
    ogFilterActive ? list.filter(item => ogIds.has(parseInt(item[idField])||item[idField])) : list;

  const filteredProfiles   = filterByOG(data.profiles??[]);
  const filteredApps       = filterByOG(data.apps??[], "LocationGroupId");
  const filteredSmartGroups= filterByOG(data.smartGroups??[]);
  const filteredTags       = (data.tags??[]).filter(t => !ogFilterActive || ogIds.has(parseInt(t.LocationGroupId)));
  const filteredProducts   = filterByOG(data.products??[]);
  const filteredOgs        = ogFilterActive ? (data.ogs??[]).filter(o => ogIds.has(o.Id?.Value)) : (data.ogs??[]);

  const OGFilterBar = () => (
    <div style={{background:C.white,border:`1px solid ${C.border}`,borderRadius:8,padding:"12px 16px",marginBottom:16}}>
      <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
        <span style={{fontWeight:700,fontSize:13,color:C.navy}}>🏢 OG Filter</span>
        {ogFilterActive&&(
          <span style={{background:C.blue+"18",color:C.blue,border:`1px solid ${C.blue}40`,borderRadius:4,padding:"1px 8px",fontSize:11,fontWeight:600}}>
            {ogFilter.size} selected
          </span>
        )}
        <button onClick={()=>setOgFilter(new Set())}
          style={{marginLeft:"auto",padding:"3px 10px",borderRadius:20,border:`1px solid ${C.border}`,
            background:C.white,color:C.muted,fontSize:11,cursor:"pointer"}}>
          Show all
        </button>
        <button onClick={()=>setOgFilter(new Set((data.ogs??[]).map(o=>o.Id?.Value)))}
          style={{padding:"3px 10px",borderRadius:20,border:`1px solid ${C.border}`,
            background:C.white,color:C.muted,fontSize:11,cursor:"pointer"}}>
          Select all
        </button>
      </div>
      <div style={{display:"flex",flexWrap:"wrap",gap:6,maxHeight:200,overflowY:"auto"}}>
        {(data.ogs??[]).map(og=>{
          const sel = ogFilterActive ? ogFilter.has(og.Id?.Value) : true;
          return(
            <button key={og.Id?.Value}
              onClick={()=>setOgFilter(prev=>{
                const next = new Set(prev.size===0 ? (data.ogs??[]).map(o=>o.Id?.Value) : prev);
                if(next.has(og.Id?.Value)) next.delete(og.Id?.Value); else next.add(og.Id?.Value);
                if(next.size===(data.ogs??[]).length) return new Set(); // alles = geen filter
                return next;
              })}
              style={{padding:"4px 10px",borderRadius:20,fontSize:11,fontWeight:500,cursor:"pointer",
                border:`1.5px solid ${sel?C.navy:C.border}`,
                background:sel?C.navy:"transparent",
                color:sel?C.white:C.muted,
                transition:"all 0.15s"}}>
              {og.Name}
              {og.LocationGroupType&&<span style={{opacity:0.6,marginLeft:4,fontSize:10}}>({og.LocationGroupType})</span>}
            </button>
          );
        })}
      </div>
    </div>
  );

  const tabs=[
    {id:"graph",    label:"🔗 Relation Graph"},
    {id:"overview", label:"📊 Overview"},
    {id:"ogs",      label:`🏢 OG's (${filteredOgs.length}${ogFilterActive?"/"+data.ogs?.length:""})`},
    {id:"settings", label:"⚙️ Settings"},
    {id:"profiles", label:`📋 Profiles (${filteredProfiles.length}${ogFilterActive?"/"+data.profiles?.length:""})`},
    {id:"apps",     label:`📦 Apps (${filteredApps.length}${ogFilterActive?"/"+data.apps?.length:""})`},
    {id:"smartgroups",label:`👥 Smart Groups (${filteredSmartGroups.length}${ogFilterActive?"/"+data.smartGroups?.length:""})`},
    {id:"tags",     label:`🏷️ Tags (${filteredTags.length}${ogFilterActive?"/"+data.tags?.length:""})`},
    {id:"products", label:`⚙️ Products (${filteredProducts.length}${ogFilterActive?"/"+data.products?.length:""})`},
    {id:"appgroups",label:`📱 App Groups (${data.appGroups?.length??0})`},
    {id:"scripts",  label:`📜 Scripts (${data.scripts?.length??0})`},
    {id:"sensors",  label:`📡 Sensors (${data.sensors?.length??0})`},
    {id:"policies", label:`🛡️ Compliance (${data.policies?.length??0})`},
    {id:"dep",      label:`🍎 DEP (${data.depProfiles?.length??0})`},
    {id:"admins",   label:`👤 Admins (${data.admins?.length??0})`},
    {id:"manual",   label:"📝 Manual"},
  ];

  return(<div style={{minHeight:"100vh",background:C.mist,fontFamily:"system-ui,sans-serif"}}>
    <div style={{background:C.navy,padding:"0 20px",display:"flex",alignItems:"center",height:54,gap:14}}>
      <div style={{width:30,height:30,background:C.teal,borderRadius:6,display:"flex",alignItems:"center",justifyContent:"center",fontSize:15}}>🏢</div>
      <div style={{color:C.white,fontWeight:700,fontSize:14,flex:1}}>
        As-Built {data._demo&&<span style={{color:C.warn,fontSize:11,marginLeft:5,fontWeight:400}}>(DEMO)</span>}
        <span style={{color:C.teal,fontFamily:"monospace",fontWeight:400,fontSize:12,marginLeft:8}}>{data.tenantUrl}</span>
      </div>
      <div style={{color:"#94A3B8",fontSize:11}}>{new Date(data.generatedAt).toLocaleString("nl-NL")}</div>
      <button onClick={exportHTML} style={{background:C.teal,color:C.white,border:"none",borderRadius:5,padding:"6px 13px",fontWeight:700,fontSize:12,cursor:"pointer"}}>⬇ Export HTML</button>
      <button onClick={()=>setStep("login")} style={{background:"transparent",color:"#94A3B8",border:"1px solid #94A3B840",borderRadius:5,padding:"6px 9px",fontSize:11,cursor:"pointer"}}>← New</button>
      <div style={{color:"#475569",fontSize:10,borderLeft:"1px solid #94A3B840",paddingLeft:10}}>© DeBruinOnline.net · Built with Claude</div>
    </div>
    <div style={{background:C.white,borderBottom:`1px solid ${C.border}`,padding:"0 20px",display:"flex",gap:1,overflowX:"auto"}}>
      {tabs.map(t=>(<button key={t.id} onClick={()=>setTab(t.id)} style={{padding:"11px 12px",border:"none",background:"transparent",cursor:"pointer",fontWeight:activeTab===t.id?700:400,color:activeTab===t.id?C.blue:C.muted,borderBottom:activeTab===t.id?`2px solid ${C.blue}`:"2px solid transparent",fontSize:11,whiteSpace:"nowrap"}}>{t.label}</button>))}
    </div>

    <div style={{padding:activeTab==="graph"?"10px 14px":20,maxWidth:activeTab==="graph"?"100%":1200,margin:"0 auto"}}>

      {activeTab==="graph"&&<RelationGraph data={data}/>}

      {activeTab==="overview"&&<div><OGFilterBar/>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))",gap:11,marginBottom:20}}>
          {[
            {label:"OG's",value:data.ogs?.length??0,icon:"🏢",color:C.navy},
            {label:"Profiles",value:filteredProfiles.length,icon:"📋",color:C.blue},
            {label:"Apps",value:filteredApps.length,icon:"📦",color:C.teal},
            {label:"Smart Groups",value:filteredSmartGroups.length,icon:"👥",color:C.warn},
            {label:"Tags",value:filteredTags.length,icon:"🏷️",color:C.purple},
            {label:"Products",value:filteredProducts.length,icon:"⚙️",color:"#0891B2"},
            {label:"Admins",value:data.admins?.length??0,icon:"👤",color:"#BE185D"},
            {label:"Users",value:data.users?.length??0,icon:"🧑",color:C.success},
          ].map(s=>(<div key={s.label} style={{background:C.white,border:`1px solid ${C.border}`,borderRadius:8,padding:"13px 15px",borderTop:`3px solid ${s.color}`}}>
            <div style={{fontSize:19}}>{s.icon}</div>
            <div style={{fontSize:24,fontWeight:800,color:s.color,lineHeight:1.1,marginTop:4}}>{s.value}</div>
            <div style={{fontSize:11,color:C.muted,marginTop:2}}>{s.label}</div>
          </div>))}
        </div>
        <Section title="OG Hiërarchie" count={data.ogs?.length} accent={C.navy}>
          <OGTree nodes={data.ogTree??[]}/>
        </Section>
        {Object.keys(data.apns??{}).length>0&&<Section title="APNs Configurations" count={Object.keys(data.apns).length} accent={C.teal}>
          {Object.entries(data.apns).map(([ogId,apns])=>{
            const og=data.ogs?.find(o=>o.Id?.Value===parseInt(ogId));
            return(<div key={ogId} style={{marginBottom:12}}>
              <div style={{fontWeight:600,color:C.navy,fontSize:13,marginBottom:6}}>🏢 {og?.Name??`OG ${ogId}`}</div>
              <KV data={apns} mono/>
            </div>);
          })}
        </Section>}
      </div>}

      {activeTab==="ogs"&&<div>
        <OGFilterBar/>
        {/* OG Hierarchy tree view */}
        <Section title="OG Hierarchy Tree" accent={C.navy} defaultOpen={true}>
          <OGTree nodes={data.ogTree??[]}/>
        </Section>
        {/* OG Details per OG */}
        {filteredOgs.map(og=>(<Section key={og.Id?.Value} title={`🏢 ${og.Name}`} accent={C.navy} defaultOpen={false}>
        <KV data={{"Name":og.Name,"Type":og.LocationGroupType,"Group ID":og.GroupId,"ID":og.Id?.Value,"UUID":og.Uuid,"Parent":og.ParentLocationGroup?.Name,"Land":og.Country}}/>
        {data.apns?.[og.Id?.Value]&&<>
          <div style={{fontWeight:600,fontSize:12,color:C.teal,marginTop:10,marginBottom:4}}>📱 APNs</div>
          <CleanKV data={data.apns[og.Id?.Value]}/>
        </>}
        {data.ogDetails?.[og.Id?.Value]&&<details style={{marginTop:10}}>
          <summary style={{cursor:"pointer",fontSize:12,color:C.blue,fontWeight:600}}>OG Settings</summary>
          <div style={{marginTop:8}}>
            <CleanKV data={data.ogDetails[og.Id?.Value]}/>
            <details style={{marginTop:6}}><summary style={{fontSize:11,color:C.muted,cursor:"pointer"}}>Show all fields including internal IDs</summary><div style={{marginTop:4}}><KV data={data.ogDetails[og.Id?.Value]} mono/></div></details>
          </div>
        </details>}
      </Section>))}</div>}

      {activeTab==="profiles"&&<div><OGFilterBar/>{filteredProfiles.map(p=>{
        const det=data.profileDetails?.[p.Id?.Value];
        const gen=det?.General;
        const assignedSGs=gen?.AssignedSmartGroups??[];
        const excludedSGs=gen?.ExcludedSmartGroups??[];
        const assignedOGs=p.AssignedOrganizationGroups?.AssignedOg??[];
        // payload keys = everything in det except General
        const payloadKeys=det?Object.keys(det).filter(k=>k!=="General"):[];
        return(<Section key={p.Id?.Value} title={`${pIcon(str(p.Platform))} ${str(p.ProfileName??p.Name)}`} accent={C.blue} defaultOpen={false}>
          {/* Algemeen */}
          <KV data={{"Platform":p.Platform,"OG":p.OrganizationGroupName??p.OrganizationGroupId,"Assignment Type":p.Type??gen?.AssignmentType,"Status":p.ProfileStatus??p.Status,"Scope":p.ProfileScope??gen?.ProfileScope,"Allow Removal":gen?.AllowRemoval,"Is Active":gen?.IsActive!=null?String(gen.IsActive):undefined,"Version":gen?.Version,"UUID":gen?.ProfileUuid,"Description":p.Description||gen?.Description}}/>

          {/* Assignments */}
          <div style={{marginTop:12}}>
            <div style={{fontWeight:700,fontSize:12,color:C.navy,marginBottom:6,paddingBottom:4,borderBottom:`1px solid ${C.border}`}}>📌 Assignments</div>
            {assignedOGs.length>0&&<div style={{marginBottom:6}}>
              <div style={{fontSize:11,fontWeight:600,color:C.muted,marginBottom:3}}>Organization Groups</div>
              {assignedOGs.map((og,i)=><div key={i} style={{fontSize:12,padding:"2px 0",color:C.text}}>🏢 {og.Name??og.OrganizationGroupName??JSON.stringify(og)}</div>)}
            </div>}
            {assignedSGs.length>0&&<div style={{marginBottom:6}}>
              <div style={{fontSize:11,fontWeight:600,color:C.muted,marginBottom:3}}>Smart Groups (assigned)</div>
              {assignedSGs.map((sg,i)=>{
                const name=typeof sg==="string"?sg.replace(/.*Name=/,"").replace(/[}].*/,""):sg.Name??sg.SmartGroupName??JSON.stringify(sg);
                return<div key={i} style={{fontSize:12,padding:"2px 0",color:C.text}}>👥 {name}</div>;
              })}
            </div>}
            {excludedSGs.length>0&&<div style={{marginBottom:6}}>
              <div style={{fontSize:11,fontWeight:600,color:C.error,marginBottom:3}}>Smart Groups (excluded)</div>
              {excludedSGs.map((sg,i)=>{
                const name=typeof sg==="string"?sg.replace(/.*Name=/,"").replace(/[}].*/,""):sg.Name??sg.SmartGroupName??JSON.stringify(sg);
                return<div key={i} style={{fontSize:12,padding:"2px 0",color:C.error}}>🚫 {name}</div>;
              })}
            </div>}
            {assignedOGs.length===0&&assignedSGs.length===0&&<div style={{fontSize:12,color:C.muted}}>Geen assignments found</div>}
          </div>

          {/* Payload settings */}
          {payloadKeys.length>0&&<div style={{marginTop:12}}>
            <div style={{fontWeight:700,fontSize:12,color:C.navy,marginBottom:6,paddingBottom:4,borderBottom:`1px solid ${C.border}`}}>⚙️ Payload Settings</div>
            {payloadKeys.map(key=>(
              <details key={key} style={{marginBottom:6}}>
                <summary style={{cursor:"pointer",fontSize:12,color:C.blue,fontWeight:600,padding:"4px 0"}}>{key}</summary>
                <div style={{marginTop:6,paddingLeft:12}}>
                  {Array.isArray(det[key])
                    ?det[key].map((item,i)=><div key={i} style={{marginBottom:8,paddingBottom:8,borderBottom:`1px solid ${C.border}`}}><KV data={item} mono/></div>)
                    :<KV data={det[key]} mono/>}
                </div>
              </details>
            ))}
          </div>}
          {!det&&<div style={{marginTop:8,fontSize:11,color:C.muted}}>⏳ Detail settings will be loaded on next sync…</div>}
        </Section>);
      })}</div>}

      {activeTab==="apps"&&<div><OGFilterBar/>
        {(()=>{
          const grouped = {};
          filteredApps.forEach(a=>{
            const type = a.AppType??APP_SOURCE[a.ApplicationSource]??"Internal";
            (grouped[type]||(grouped[type]=[])).push(a);
          });
          const order = ["Internal","Public","Purchased"];
          const allTypes = [...new Set([...order, ...Object.keys(grouped)])];
          return allTypes.map(type=>{
            const apps = grouped[type]||[];
            if(!apps.length) return null;
            const accent = type==="Internal"?C.teal:type==="Public"?C.sky:C.success;
            return(
              <Section key={type} title={`${type} Apps (${apps.length})`} accent={accent} defaultOpen={type==="Internal"}>
                {apps.map((a,i)=>{
                  const platform = PLATFORM_MAP[a.Platform]??str(a.Platform);
                  const pushMode = PUSH_MODE[a.PushMode]??"On Demand";
                  const smartGroups = a.SmartGroups??[];
                  const models = a.SupportedModels?.Model??[];
                  const msi = a.MsiDeploymentParameters;
                  const categories = a.CategoryList?.Category??[];
                  return(
                    <div key={i} style={{background:C.white,border:`1px solid ${C.border}`,borderRadius:8,padding:"12px 16px",marginBottom:10}}>
                      {/* Header */}
                      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
                        <span style={{fontSize:20}}>{pIcon(platform)}</span>
                        <div style={{flex:1}}>
                          <div style={{fontWeight:700,fontSize:14,color:C.navy}}>{str(a.ApplicationName??a.AppName)}</div>
                          {a.DisplayName&&a.DisplayName!==a.ApplicationName&&<div style={{fontSize:11,color:C.muted}}>{a.DisplayName}</div>}
                        </div>
                        <div style={{display:"flex",gap:4,flexWrap:"wrap",justifyContent:"flex-end"}}>
                          {a.AppVersion&&badge(`v${a.AppVersion}`,C.teal)}
                          {badge(platform,C.sky)}
                          {badge(a.Status??"",a.Status==="Active"?C.success:C.warn)}
                          {badge(pushMode, pushMode==="Auto"?C.blue:pushMode==="Required"?C.error:C.muted)}
                        </div>
                      </div>

                      {/* Details grid */}
                      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0 20px"}}>
                        <KV data={{
                          "Bundle ID": a.BundleId??a.AppIdentifier,
                          "Bestandsnaam": a.ApplicationFileName,
                          "Bestandsgrootte": a.ApplicationSize,
                          "Version (actueel)": a.ActualFileVersion,
                          "Version-ID": a.VersionIdentifier,
                        }}/>
                        <KV data={{
                          "OG": a.RootLocationGroupName??a.OrganizationGroupName,
                          "OG-ID": a.LocationGroupId,
                          "Assignment": a.AssignmentStatus,
                          "Auto-update": a.AutoUpdateVersion?"Ja":"Nee",
                          "Provisioning": a.EnableProvisioning?"Ja":"Nee",
                        }}/>
                      </div>

                      {/* Device stats */}
                      {(a.AssignedDeviceCount!=null||a.InstalledDeviceCount!=null)&&(
                        <div style={{display:"flex",gap:12,marginTop:10,padding:"8px 12px",background:C.mist,borderRadius:6}}>
                          {[
                            {label:"Assigned",value:a.AssignedDeviceCount??0,color:C.blue},
                            {label:"Installed",value:a.InstalledDeviceCount??0,color:C.success},
                            {label:"Not Installed",value:a.NotInstalledDeviceCount??0,color:C.warn},
                          ].map(s=>(
                            <div key={s.label} style={{textAlign:"center",flex:1}}>
                              <div style={{fontSize:20,fontWeight:800,color:s.color}}>{s.value}</div>
                              <div style={{fontSize:10,color:C.muted}}>{s.label}</div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Smart Groups */}
                      {smartGroups.length>0&&(
                        <div style={{marginTop:10}}>
                          <div style={{fontSize:11,fontWeight:700,color:C.muted,marginBottom:4}}>👥 Smart Groups ({smartGroups.length})</div>
                          <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                            {smartGroups.map((sg,si)=>(
                              <span key={si} style={{background:C.warn+"18",color:C.warn,border:`1px solid ${C.warn}40`,borderRadius:4,padding:"2px 8px",fontSize:11}}>
                                {sg.Name??`SG ${sg.Id}`}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Supported models */}
                      {models.length>0&&(
                        <div style={{marginTop:8}}>
                          <div style={{fontSize:11,fontWeight:700,color:C.muted,marginBottom:4}}>📱 Supported Models</div>
                          <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                            {models.map((m,mi)=>badge(m.ModelName??m.ModelId, C.sky))}
                          </div>
                        </div>
                      )}

                      {/* MSI deployment parameters */}
                      {msi&&msi.InstallContext&&(
                        <details style={{marginTop:10}}>
                          <summary style={{cursor:"pointer",fontSize:12,color:C.blue,fontWeight:600}}>MSI Deployment Parameters</summary>
                          <div style={{marginTop:6,paddingLeft:12}}>
                            <KV data={{
                              "Install Context": msi.InstallContext,
                              "Timeout (min)": msi.InstallTimeoutInMinutes||"Standaard",
                              "Retry Count": msi.RetryCount,
                              "Retry Interval (min)": msi.RetryIntervalInMinutes,
                            }}/>
                          </div>
                        </details>
                      )}

                      {/* Categories */}
                      {categories.length>0&&(
                        <div style={{marginTop:8}}>
                          <div style={{fontSize:11,fontWeight:700,color:C.muted,marginBottom:4}}>Categorieën</div>
                          <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                            {categories.map((cat,ci)=>badge(cat.Name??cat,C.purple))}
                          </div>
                        </div>
                      )}

                      {/* Launch info */}
                      {a.LaunchCommand&&(
                        <div style={{marginTop:8,padding:"6px 10px",background:C.navy+"0a",borderRadius:4}}>
                          <span style={{fontSize:11,color:C.muted,fontWeight:600}}>Launch: </span>
                          <code style={{fontSize:11}}>{a.LaunchCommand}</code>
                        </div>
                      )}
                    </div>
                  );
                })}
              </Section>
            );
          });
        })()}
      </div>}

      {activeTab==="smartgroups"&&<div><OGFilterBar/>{filteredSmartGroups.map(sg=>(<Section key={sg.SmartGroupID} title={`👥 ${sg.Name}`} accent={C.warn} defaultOpen={false}>
        <KV data={{"ID":sg.SmartGroupID,"UUID":sg.SmartGroupUuid,"Criteria Type":sg.CriteriaType,"Devices":sg.Devices??sg.DeviceCount,"Assignments":sg.Assignments,"Exclusions":sg.Exclusions,"Managed by OG":sg.ManagedByOrganizationGroupName??sg.OrganizationGroupName,"Ownerships":(sg.Ownerships||[]).join(", ")||undefined,"Tags":(sg.Tags||[]).map(t=>t.Name||t).join(", ")||undefined}}/>
        {(()=>{
          // sgDetails contains the full criteria - same as sg itself for this API version
          const det = data.sgDetails?.[sg.SmartGroupID] || sg;
          const criteria = [
            ...(det.OrganizationGroups||[]).map(o=>`OG: ${o.Name}`),
            ...(det.UserGroups||[]).map(g=>`UG: ${g.Name||g}`),
            ...(det.Tags||[]).map(t=>`Tag: ${t.Name||t}`),
            ...(det.Ownerships||[]).map(o=>`Ownership: ${o}`),
            ...(det.Platforms||[]).map(p=>`Platform: ${p.Name||p}`),
            ...(det.Models||[]).map(m=>`Model: ${m.Name||m}`),
            ...(det.UserAdditions||[]).map(u=>`User+: ${u.Name||u}`),
            ...(det.DeviceAdditions||[]).map(d=>`Device+: ${d.Name||d}`),
            ...(det.DeviceExclusions||[]).map(d=>`Device-: ${d.Name||d}`),
            ...(det.UserExclusions||[]).map(u=>`User-: ${u.Name||u}`),
          ].filter(Boolean);
          return criteria.length>0?(
            <div style={{marginTop:10}}>
              <div style={{fontSize:12,fontWeight:700,color:C.muted,marginBottom:6}}>Criteria</div>
              <div style={{display:"flex",flexWrap:"wrap",gap:4}}>
                {criteria.map((cr,i)=><span key={i} style={{background:C.mist,border:`1px solid ${C.border}`,borderRadius:4,padding:"2px 8px",fontSize:11}}>{cr}</span>)}
              </div>
            </div>
          ):null;
        })()}
      </Section>))}</div>}

      {activeTab==="tags"&&<div><OGFilterBar/>
        {filteredOgs.map(og=>{
          const ogTags=filteredTags.filter(t=>parseInt(t.LocationGroupId)===og.Id?.Value);
          if(!ogTags.length)return null;
          return(<Section key={og.Id?.Value} title={`🏢 ${og.Name} (${ogTags.length} tags)`} accent={C.purple} defaultOpen={false}>
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
              <thead><tr style={{background:C.mist}}><th style={{padding:"6px 8px",textAlign:"left",border:`1px solid ${C.border}`}}>Tag Name</th><th style={{padding:"6px 8px",textAlign:"left",border:`1px solid ${C.border}`}}>Type</th><th style={{padding:"6px 8px",textAlign:"left",border:`1px solid ${C.border}`}}>ID</th></tr></thead>
              <tbody>{ogTags.map((t,i)=>(<tr key={i} style={{borderBottom:`1px solid ${C.border}`}}><td style={{padding:"5px 8px"}}>{t.TagName}</td><td style={{padding:"5px 8px",color:C.muted}}>{t.TagType}</td><td style={{padding:"5px 8px",color:C.muted,fontFamily:"monospace"}}>{t.TagId??t.Id?.Value}</td></tr>))}</tbody>
            </table>
          </Section>);
        })}
        {!filteredTags.length&&<div style={{background:C.white,border:`1px solid ${C.border}`,borderRadius:8,padding:20,color:C.muted}}>Geen tags found.</div>}
      </div>}

      {activeTab==="products"&&<div><OGFilterBar/>
        {!filteredProducts.length?<div style={{background:C.white,border:`1px solid ${C.border}`,borderRadius:8,padding:20,color:C.muted}}>Geen products found.</div>:
        filteredProducts.map((p,i)=>(<Section key={i} title={`⚙️ ${p.Name}`} accent="#0891B2" defaultOpen={false}>
          <KV data={{"Platform":p.Platform,"Status":p.Status,"OG":p.OrganizationGroupName,"ID":p.ID}}/>
          {data.productDetails?.[p.ID]&&<details style={{marginTop:10}}><summary style={{cursor:"pointer",fontSize:12,color:C.blue,fontWeight:600}}>Product details</summary><div style={{marginTop:8}}><KV data={data.productDetails[p.ID]} mono/></div></details>}
        </Section>))}
      </div>}

      {activeTab==="admins"&&<div>
        <Section title={`Admins (${data.admins?.length??0})`} accent="#BE185D">
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
            <thead><tr style={{background:C.mist}}>{["Username","Name","Email","OG","Rol"].map(h=>(<th key={h} style={{padding:"6px 8px",textAlign:"left",border:`1px solid ${C.border}`}}>{h}</th>))}</tr></thead>
            <tbody>{(data.admins??[]).map((a,i)=>(<tr key={i} style={{borderBottom:`1px solid ${C.border}`}}>
              <td style={{padding:"5px 8px",fontFamily:"monospace"}}>{a.UserName}</td>
              <td style={{padding:"5px 8px"}}>{a.FirstName} {a.LastName}</td>
              <td style={{padding:"5px 8px",color:C.muted}}>{a.Email}</td>
              <td style={{padding:"5px 8px"}}>{a.LocationGroup}</td>
              <td style={{padding:"5px 8px"}}>{a.RoleName??a.Role??""}</td>
            </tr>))}</tbody>
          </table>
        </Section>
        <Section title={`User Groups (${data.userGroups?.length??0})`} accent={C.success} defaultOpen={false}>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
            <thead><tr style={{background:C.mist}}>{["Name","Type","Gebruikers","OG"].map(h=>(<th key={h} style={{padding:"6px 8px",textAlign:"left",border:`1px solid ${C.border}`}}>{h}</th>))}</tr></thead>
            <tbody>{(data.userGroups??[]).map((g,i)=>(<tr key={i} style={{borderBottom:`1px solid ${C.border}`}}>
              <td style={{padding:"5px 8px"}}>{g.UserGroupName??g.Name}</td>
              <td style={{padding:"5px 8px",color:C.muted}}>{g.GroupType??g.Type}</td>
              <td style={{padding:"5px 8px"}}>{g.UserCount??g.UsersCount}</td>
              <td style={{padding:"5px 8px"}}>{g.OrganizationGroupName}</td>
            </tr>))}</tbody>
          </table>
        </Section>
      </div>}

      {activeTab==="settings"&&<div>
        <OGFilterBar/>
        {filteredOgs.map(og=>{
          const ogId=og.Id?.Value;
          const apns=data.apns?.[ogId];
          const ldap=data.ldapConfigs?.[ogId];
          const sr=data.sampleRates?.[ogId];
          const ca=data.customAttributes?.[ogId];
          const acc=data.accStatus?.[ogId];
          const hasData=apns||ldap||sr||ca||acc;
          if(!hasData) return null;
          return(<Section key={ogId} title={`🏢 ${og.Name}`} accent={C.navy} defaultOpen={false}>

            {apns&&<details style={{marginBottom:10}}>
              <summary style={{cursor:"pointer",fontSize:13,fontWeight:700,color:C.teal,padding:"6px 0"}}>📱 APNs Configuration</summary>
              <div style={{marginTop:8,paddingLeft:12}}>
                <KV data={{
                  "Apple ID": apns.AppleId??apns.AppleID,
                  "Certificate Expiry": apns.ExpirationDate??apns.CertificateExpirationDate,
                  "Subject": apns.Subject??apns.CertificateSubject,
                  "Topic": apns.Topic??apns.CertificateTopic,
                  "Status": apns.Status??apns.CertificateStatus,
                  "Push Endpoint": apns.PushEndpoint,
                }}/>
                <details style={{marginTop:6}}><summary style={{cursor:"pointer",fontSize:11,color:C.muted}}>Full APNs response</summary><div style={{marginTop:4}}><KV data={apns} mono/></div></details>
              </div>
            </details>}

            {ldap&&<details style={{marginBottom:10}}>
              <summary style={{cursor:"pointer",fontSize:13,fontWeight:700,color:C.blue,padding:"6px 0"}}>🔗 LDAP / Directory Services</summary>
              <div style={{marginTop:8,paddingLeft:12}}>
                <KV data={{"Server":ldap.server_config?.host??ldap.ServerConfig?.Host,"Port":ldap.server_config?.port??ldap.ServerConfig?.Port,"Protocol":ldap.server_config?.protocol??ldap.ServerConfig?.Protocol,"Bind DN":ldap.user_config?.bind_dn??ldap.UserConfig?.BindDistinguishedName,"Base DN":ldap.user_config?.base_dn??ldap.UserConfig?.BaseDN,"UUID":ldap.uuid}}/>
                <details style={{marginTop:8}}><summary style={{cursor:"pointer",fontSize:11,color:C.muted}}>Volledige LDAP configuratie</summary><div style={{marginTop:6}}><KV data={ldap} mono/></div></details>
              </div>
            </details>}

            {acc&&<details style={{marginBottom:10}}>
              <summary style={{cursor:"pointer",fontSize:13,fontWeight:700,color:C.success,padding:"6px 0"}}>☁️ Cloud Connector (ACC) Status</summary>
              <div style={{marginTop:8,paddingLeft:12}}><KV data={acc}/></div>
            </details>}

            {sr&&<details style={{marginBottom:10}}>
              <summary style={{cursor:"pointer",fontSize:13,fontWeight:700,color:C.warn,padding:"6px 0"}}>📊 Device Sample Rates</summary>
              <div style={{marginTop:8,paddingLeft:12}}><KV data={sr}/></div>
            </details>}

            {ca&&ca.length>0&&<details style={{marginBottom:10}}>
              <summary style={{cursor:"pointer",fontSize:13,fontWeight:700,color:C.purple,padding:"6px 0"}}>🏷️ Custom Attributes ({ca.length})</summary>
              <div style={{marginTop:8,paddingLeft:12}}>
                {ca.map((attr,i)=><div key={i} style={{padding:"4px 0",borderBottom:`1px solid ${C.border}`,fontSize:12}}><strong>{attr.Name??attr.name}</strong><span style={{color:C.muted,marginLeft:8}}>{attr.DataType??attr.dataType??attr.Type}</span></div>)}
              </div>
            </details>}
          </Section>);
        })}

        {/* Globale settings */}
        {data.enrollmentRestrictions&&<Section title="📋 Enrollment Restrictions" accent={C.blue} defaultOpen={false}>
          <KV data={data.enrollmentRestrictions} mono/>
        </Section>}

        {Object.keys(data.directEnrollment||{}).length>0&&<Section title="📲 Workspace ONE Direct Enrollment" accent={C.blue} defaultOpen={false}>
          {Object.entries(data.directEnrollment||{}).map(([ogId,de])=>{
            const og=data.ogs?.find(o=>o.Id?.Value===parseInt(ogId));
            return(<div key={ogId} style={{marginBottom:12}}>
              <div style={{fontWeight:600,color:C.navy,fontSize:13,marginBottom:6}}>🏢 {og?.Name||`OG ${ogId}`}</div>
              <KV data={{
                "WS1 Direct Enrollment": de.enableWorkSpaceOneDirectEnrollment!=null?String(de.enableWorkSpaceOneDirectEnrollment):undefined,
                "Android Enterprise": de.enableWorkSpaceOneDirectEnrollmentForAndroidEnterprise!=null?String(de.enableWorkSpaceOneDirectEnrollmentForAndroidEnterprise):undefined,
                "Android Legacy": de.enableWorkSpaceOneDirectEnrollmentForAndroidLegacy!=null?String(de.enableWorkSpaceOneDirectEnrollmentForAndroidLegacy):undefined,
                "iOS": de.enableWorkSpaceOneDirectEnrollmentForAppleIos!=null?String(de.enableWorkSpaceOneDirectEnrollmentForAppleIos):undefined,
                "macOS": de.enableWorkSpaceOneDirectEnrollmentForAppleMacOs!=null?String(de.enableWorkSpaceOneDirectEnrollmentForAppleMacOs):undefined,
                "Windows": de.enableWorkSpaceOneDirectEnrollmentForWindows!=null?String(de.enableWorkSpaceOneDirectEnrollmentForWindows):undefined,
                "Assigned User Group": de.assignedUserGroup||undefined,
              }}/>
            </div>);
          })}
        </Section>}

        {Object.keys(data.androidWork||{}).length>0&&<Section title="🤖 Android Enterprise (Work)" accent="#16A34A" defaultOpen={false}>
          {Object.entries(data.androidWork||{}).map(([ogId,aw])=>{
            const og=data.ogs?.find(o=>o.Id?.Value===parseInt(ogId));
            return(<div key={ogId} style={{marginBottom:12}}>
              <div style={{fontWeight:600,color:C.navy,fontSize:13,marginBottom:6}}>🏢 {og?.Name||`OG ${ogId}`}</div>
              <KV data={{
                "Enterprise ID": aw.EnterpriseId,
                "Service Account Email": aw.ServiceAccountEmail,
                "Service Account Admin": aw.ServiceAccountAdminEmail,
                "Client ID": aw.ServiceAccountClientId,
              }}/>
            </div>);
          })}
        </Section>}

        {data.vidmConfig&&<Section title="🔐 Workspace ONE Access / VIDM" accent={C.purple} defaultOpen={false}>
          <KV data={{
            "VIDM Server URL": data.vidmConfig.vidmServerUrl,
            "VIDM Configured": data.vidmConfig.isVidmConfigured!=null?String(data.vidmConfig.isVidmConfigured):undefined,
            "GreenBox Enabled": data.vidmConfig.isGreenBoxCatalogEnabled!=null?String(data.vidmConfig.isGreenBoxCatalogEnabled):undefined,
            "GreenBox Server": data.vidmConfig.greenBoxServerUrl||undefined,
          }}/>
          <details style={{marginTop:8}}><summary style={{cursor:"pointer",fontSize:11,color:C.muted}}>Full response</summary><div style={{marginTop:6}}><KV data={data.vidmConfig} mono/></div></details>
        </Section>}

        {Object.keys(data.storageInfo||{}).length>0&&<Section title="💾 Storage Capacity" accent={C.teal} defaultOpen={false}>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
            <thead><tr style={{background:C.mist}}>
              {["OG","App Capacity (MB)","App Max File (MB)","Content Capacity (MB)","Content Max File (MB)"].map(h=>(
                <th key={h} style={{padding:"6px 8px",textAlign:"left",border:`1px solid ${C.border}`}}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {Object.entries(data.storageInfo).map(([ogId,s])=>{
                const og=data.ogs?.find(o=>o.Id?.Value===parseInt(ogId));
                return(<tr key={ogId} style={{borderBottom:`1px solid ${C.border}`}}>
                  <td style={{padding:"5px 8px",fontWeight:500}}>{og?.Name||`OG ${ogId}`}</td>
                  <td style={{padding:"5px 8px",textAlign:"right"}}>{s.ApplicationCapacity}</td>
                  <td style={{padding:"5px 8px",textAlign:"right"}}>{s.ApplicationMaxFileSize}</td>
                  <td style={{padding:"5px 8px",textAlign:"right"}}>{s.ContentCapacity}</td>
                  <td style={{padding:"5px 8px",textAlign:"right"}}>{s.ContentMaxFileSize}</td>
                </tr>);
              })}
            </tbody>
          </table>
        </Section>}

        {data.licenses?.length>0&&<Section title={`🔑 Product Licenses (${data.licenses.length})`} accent={C.teal} defaultOpen={false}>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
            <thead><tr style={{background:C.mist}}>{["Product","Type","Totaal","Gebruikt","Beschikbaar"].map(h=>(<th key={h} style={{padding:"6px 8px",textAlign:"left",border:`1px solid ${C.border}`}}>{h}</th>))}</tr></thead>
            <tbody>{data.licenses.map((l,i)=>(<tr key={i} style={{borderBottom:`1px solid ${C.border}`}}>
              <td style={{padding:"5px 8px"}}>{l.ProductName??l.Name??l.Type}</td>
              <td style={{padding:"5px 8px",color:C.muted}}>{l.LicenseType??l.Type}</td>
              <td style={{padding:"5px 8px"}}>{l.TotalLicenses??l.Total??"-"}</td>
              <td style={{padding:"5px 8px"}}>{l.UsedLicenses??l.Used??"-"}</td>
              <td style={{padding:"5px 8px",color:C.success}}>{l.AvailableLicenses??l.Available??"-"}</td>
            </tr>))}</tbody>
          </table>
        </Section>}

        {data.certificateAuthorities?.length>0&&<Section title={`🔐 Certificate Authorities (${data.certificateAuthorities.length})`} accent={C.error} defaultOpen={false}>
          {data.certificateAuthorities.map((ca,i)=>{
            const caId=ca.Key??ca.Id;
            const templates=data.caTemplates?.[caId];
            return(<div key={i} style={{marginBottom:10,padding:10,border:`1px solid ${C.border}`,borderRadius:6}}>
              <div style={{fontWeight:700,fontSize:13,marginBottom:6}}>🔐 {ca.Description??ca.Name??`CA ${caId}`}</div>
              <KV data={{"ID":caId,"Name":ca.Description??ca.Name}}/>
              {templates&&<div style={{marginTop:8}}>
                <div style={{fontSize:11,fontWeight:700,color:C.muted,marginBottom:4}}>Templates ({Array.isArray(templates)?templates.length:0})</div>
                {(Array.isArray(templates)?templates:[]).map((t,ti)=>(
                  <div key={ti} style={{fontSize:12,padding:"3px 0",borderBottom:`1px solid ${C.border}`}}>{t.Description??t.Name??t.Value}</div>
                ))}
              </div>}
            </div>);
          })}
        </Section>}
      </div>}

      {activeTab==="appgroups"&&<div>
        {!data.appGroups?.length
          ?<div style={{background:C.white,border:`1px solid ${C.border}`,borderRadius:8,padding:20,color:C.muted}}>No App Groups found.</div>
          :(data.appGroups??[]).map((ag,i)=>(
            <Section key={i} title={`📱 ${ag.Name??ag.ApplicationGroupName??`App Group ${i+1}`}`} accent={C.sky} defaultOpen={false}>
              <KV data={{
                "Name": ag.Name??ag.ApplicationGroupName,
                "Description": ag.Description,
                "OG": ag.OrganizationGroupName??ag.LocationGroupName,
                "App Count": ag.ApplicationCount??ag.NumberOfApps,
                "ID": ag.Id?.Value??ag.ApplicationGroupId,
              }}/>
            </Section>
          ))}
      </div>}

      {activeTab==="scripts"&&<div>
        {!data.scripts?.length
          ?<div style={{background:C.white,border:`1px solid ${C.border}`,borderRadius:8,padding:20,color:C.muted}}>Geen scripts found.</div>
          :(data.scripts??[]).map((s,i)=>(
            <Section key={i} title={`📜 ${s.name??s.Name??`Script ${i+1}`}`} accent={C.blue} defaultOpen={false}>
              <KV data={{"Platform":s.platform??s.Platform,"Type":s.script_type??s.type,"OG":s.ogName??s.organization_group_name,"Taal":s.execution_context??s.ExecutionContext,"UUID":s.script_uuid??s.uuid,"Version":s.version}}/>
            </Section>
          ))}
      </div>}

      {activeTab==="sensors"&&<div>
        {!data.sensors?.length
          ?<div style={{background:C.white,border:`1px solid ${C.border}`,borderRadius:8,padding:20,color:C.muted}}>Geen sensors found.</div>
          :(data.sensors??[]).map((s,i)=>(
            <Section key={i} title={`📡 ${s.name??s.Name??`Sensor ${i+1}`}`} accent={C.teal} defaultOpen={false}>
              <KV data={{"Description":s.description,"Platform":s.platform,"Type":s.query_type??s.QueryType,"OG":s.ogName??s.organization_group_name,"UUID":s.uuid,"Query":s.query_response??s.query}}/>
            </Section>
          ))}
      </div>}

      {activeTab==="policies"&&<div>
        {!data.policies?.length
          ?<div style={{background:C.white,border:`1px solid ${C.border}`,borderRadius:8,padding:20,color:C.muted}}>Geen compliance policies found.</div>
          :(data.policies??[]).map((p,i)=>(
            <Section key={i} title={`🛡️ ${p.name??p.Name??p.PolicyName??`Policy ${i+1}`}`} accent={C.success} defaultOpen={false}>
              <KV data={{"Name":p.name??p.Name,"Platform":p.platform??p.Platform,"Actief":p.isActive!=null?String(p.isActive):p.IsActive,"ID":p.devicePolicyId??p.Id?.Value,"Description":p.description}}/>
              {(p.complianceActions||p.Actions||p.RuleSet)&&<details style={{marginTop:10}}>
                <summary style={{cursor:"pointer",fontSize:12,color:C.blue,fontWeight:600}}>Rules & Actions</summary>
                <div style={{marginTop:8}}><KV data={p} mono/></div>
              </details>}
            </Section>
          ))}
      </div>}

      {activeTab==="dep"&&<div>
        {!data.depProfiles?.length
          ?<div style={{background:C.white,border:`1px solid ${C.border}`,borderRadius:8,padding:20,color:C.muted}}>Geen DEP profiles found.</div>
          :(data.depProfiles??[]).map((p,i)=>(
            <Section key={i} title={`🍎 ${p.ProfileName??p.Name??`DEP Profile ${i+1}`}`} accent="#555555" defaultOpen={false}>
              <KV data={{"Profile Name":p.ProfileName,"Department":p.Department,"Support Telefoon":p.SupportPhoneNumber,"Support Email":p.SupportEmailAddress,"Is Supervised":p.IsSupervisedDevice!=null?String(p.IsSupervisedDevice):undefined,"Allow Remove":p.AllowRemoveProfile!=null?String(p.AllowRemoveProfile):undefined,"UUID":p.ProfileUuid??p.SharedMdmProfileDataId}}/>
            </Section>
          ))}
      </div>}

      {activeTab==="manual"&&<div>
        <div style={{background:"#FFF3CD",border:"1px solid #F59E0B",borderRadius:8,padding:"14px 18px",marginBottom:16,fontSize:13}}>
          <strong>📝 Console-only instellingen</strong> — De onderstaande instellingen zijn niet beschikbaar via de REST API en moeten handmatig worden gedocumenteerd vanuit de UEM Console.
        </div>
        {[
          {cat:"Devices & Users — General",items:[
            ["Enrollment Settings","Groups & Settings → All Settings → Devices & Users → General → Enrollment"],
            ["Enrollment Restrictions","Groups & Settings → All Settings → Devices & Users → General → Enrollment Restrictions"],
            ["Exchange / EAS","Groups & Settings → All Settings → Devices & Users → General → Exchange"],
            ["Passcode Policy","Groups & Settings → All Settings → Devices & Users → General → Passcode"],
            ["Privacy Settings","Groups & Settings → All Settings → Devices & Users → General → Privacy"],
          ]},
          {cat:"Apple",items:[
            ["APNs Certificaten","Groups & Settings → All Settings → Devices & Users → Apple → APNs For MDM"],
            ["DEP / Apple Business Manager","Groups & Settings → All Settings → Devices & Users → Apple → Apple DEP"],
            ["VPP / Apple Apps & Books","Groups & Settings → All Settings → Devices & Users → Apple → VPP Managed Distribution"],
          ]},
          {cat:"Android",items:[
            ["Android EMM Registration","Groups & Settings → All Settings → Devices & Users → Android → Android EMM Registration"],
            ["Work Profile Settings","Groups & Settings → All Settings → Devices & Users → Android → Work Profile"],
          ]},
          {cat:"Windows",items:[
            ["WNS (Windows Notification)","Groups & Settings → All Settings → Devices & Users → Windows → WNS"],
            ["BIOS Management","Groups & Settings → All Settings → Devices & Users → Windows → BIOS"],
          ]},
          {cat:"Enterprise Integration",items:[
            ["LDAP / Active Directory","Groups & Settings → All Settings → System → Enterprise Integration → Directory Services"],
            ["SMTP Configuratie","Groups & Settings → All Settings → System → Enterprise Integration → Email (SMTP)"],
            ["Relay Servers","Groups & Settings → All Settings → System → Enterprise Integration → Relay Servers"],
            ["Content Gateway","Groups & Settings → All Settings → Content → Content Gateway"],
            ["Kerberos","Groups & Settings → All Settings → Devices & Users → Advanced → Kerberos"],
          ]},
          {cat:"Beveiliging",items:[
            ["SCEP / Certificate Authority","Groups & Settings → All Settings → System → Enterprise Integration → Certificate Authorities"],
            ["SSO Configuratie","Groups & Settings → All Settings → System → Security → SSO"],
            ["Custom Attributes","Groups & Settings → All Settings → Devices & Users → Advanced → Custom Attributes"],
          ]},
          {cat:"Systeem",items:[
            ["Branding","Groups & Settings → All Settings → System → Branding"],
            ["Roles & Permissions","Accounts → Administrators → Roles"],
            ["Event Notifications / Webhooks","Groups & Settings → All Settings → System → Advanced → Event Notifications"],
            ["Compliance Policies","Devices → Compliance Policies"],
          ]},
        ].map(({cat,items})=>(<Section key={cat} title={cat} accent={C.warn} defaultOpen={false}>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
            <thead><tr style={{background:C.mist}}><th style={{padding:"6px 8px",textAlign:"left",border:`1px solid ${C.border}`}}>Instelling</th><th style={{padding:"6px 8px",textAlign:"left",border:`1px solid ${C.border}`}}>Pad in Console</th><th style={{padding:"6px 8px",textAlign:"center",border:`1px solid ${C.border}`,width:80}}>✓</th></tr></thead>
            <tbody>{items.map(([s,p],i)=>(<tr key={i} style={{borderBottom:`1px solid ${C.border}`}}>
              <td style={{padding:"5px 8px",fontWeight:500}}>{s}</td>
              <td style={{padding:"5px 8px",color:C.muted,fontSize:11,fontFamily:"monospace"}}>{p}</td>
              <td style={{padding:"5px 8px",textAlign:"center",fontSize:16}}>☐</td>
            </tr>))}</tbody>
          </table>
        </Section>))}
      </div>}

    </div>
  </div>);
}
